import React, { useState, useEffect, useMemo } from 'react';
import './App.css';
import './auth.css';
import './styles/birthday.css';
import { students as defaultStudents, classInfo, behaviorIssues, classEconomy, NORMAL_DAY_QUOTE_TEXT, NORMAL_DAY_QUOTE_AUTHOR } from './data';
import { playCrowdWarning, playCheer } from "./utils/soundEngine";
import { useTheme } from './hooks/useTheme';
import { checkBirthdayPriority, getBirthdayCardMessage } from './services/birthdayService';
import HeroBanner from './components/HeroBanner';
import LoginCard from './components/LoginCard';
import AdminPanel from './components/AdminPanel';

// Test modülü (development için) - devre dışı
// if (process.env.NODE_ENV === 'development') {
//   import('./test/birthdayTest.js');
// }

// Güncellenmiş öğrenci listesini al - güvenli JSON parsing ile
const getUpdatedStudents = () => {
  try {
    const saved = localStorage.getItem('updatedStudents');
    return saved ? JSON.parse(saved) : defaultStudents;
  } catch (error) {
    console.error('Error parsing student data:', error);
    return defaultStudents;
  }
};

const students = getUpdatedStudents();

// Story Exam View Component
function StoryExamView({ students }) {
  const [activeTab, setActiveTab] = useState('entry');
  const [editingExam, setEditingExam] = useState(null);
  const [examData, setExamData] = useState({
    date: new Date().toISOString().split('T')[0],
    weekNumber: '',
    studentScores: students.map(student => ({
      studentId: student.id,
      score: 0
    }))
  });

  const calculateEconomyPoints = (score) => {
    let points = Math.floor(score / 10);
    if (score >= 90) points += 5;
    else if (score >= 80) points += 3;
    return points;
  };

  const updateStudentScore = (studentId, value) => {
    setExamData(prev => ({
      ...prev,
      studentScores: prev.studentScores.map(score => 
        score.studentId === studentId 
          ? { ...score, score: Math.max(0, Math.min(100, parseInt(value) || 0)) }
          : score
      )
    }));
  };

  const saveStoryExam = () => {
    if (!examData.weekNumber) {
      alert('Hikaye numarası giriniz!');
      return;
    }

    const hasValidScores = examData.studentScores.some(score => score.score > 0);
    if (!hasValidScores) {
      alert('En az bir öğrenci için puan giriniz!');
      return;
    }

    try {
      const processedExamData = {
        id: Date.now(),
        date: examData.date,
        weekNumber: parseInt(examData.weekNumber),
        studentScores: examData.studentScores.map(score => ({
          ...score,
          economyPoints: calculateEconomyPoints(score.score)
        }))
      };

      const exams = JSON.parse(localStorage.getItem('storyExams') || '[]');
      exams.push(processedExamData);
      localStorage.setItem('storyExams', JSON.stringify(exams));
      setSavedExams(exams);

      const currentEconomy = JSON.parse(localStorage.getItem('economy') || '{}');
      processedExamData.studentScores.filter(score => score.score > 0).forEach(score => {
        const current = currentEconomy[`earnings-${score.studentId}`] || 0;
        currentEconomy[`earnings-${score.studentId}`] = current + score.economyPoints;
      });
      localStorage.setItem('economy', JSON.stringify(currentEconomy));
      window.dispatchEvent(new Event('economyUpdated'));

      alert('✅ Hikaye sınavı kaydedildi ve ekonomi puanları eklendi!');
      
      setExamData({
        date: new Date().toISOString().split('T')[0],
        weekNumber: '',
        studentScores: students.map(student => ({ studentId: student.id, score: 0 }))
      });
    } catch (error) {
      console.error('Hikaye sınavı kaydetme hatası:', error);
      alert('❌ Sınav kaydedilirken hata oluştu!');
    }
  };

  const updateStoryExam = (examId, updatedStudentScores) => {
    try {
      let exams = JSON.parse(localStorage.getItem('storyExams') || '[]');
      const examIndex = exams.findIndex(e => e.id === examId);
      
      if (examIndex === -1) return;
      
      const oldScores = exams[examIndex].studentScores;
      const newScores = updatedStudentScores;
      const currentEconomy = JSON.parse(localStorage.getItem('economy') || '{}');
      
      newScores.forEach(newScore => {
        const oldScore = oldScores.find(old => old.studentId === newScore.studentId);
        const oldPoints = oldScore?.economyPoints || 0;
        const newPoints = newScore.economyPoints;
        const pointDifference = newPoints - oldPoints;
        
        if (pointDifference !== 0) {
          const current = currentEconomy[`earnings-${newScore.studentId}`] || 0;
          currentEconomy[`earnings-${newScore.studentId}`] = current + pointDifference;
        }
      });
      
      localStorage.setItem('economy', JSON.stringify(currentEconomy));
      window.dispatchEvent(new Event('economyUpdated'));
      
      exams[examIndex].studentScores = newScores;
      localStorage.setItem('storyExams', JSON.stringify(exams));
      setSavedExams(exams);
      
      alert('Sınav başarıyla güncellendi ve ekonomi puanları düzeltildi!');
      setEditingExam(null);
    } catch (error) {
      console.error('Error updating exam:', error);
      alert('Sınav güncellenirken hata oluştu!');
    }
  };

  const [savedExams, setSavedExams] = useState([]);
  
  useEffect(() => {
    try {
      const exams = JSON.parse(localStorage.getItem('storyExams') || '[]');
      setSavedExams(exams);
    } catch (error) {
      console.error('Error loading exams:', error);
      setSavedExams([]);
    }
  }, []);

  return (
    <div className="weekly-test-view">
      <h2>📖 Hikaye Sınavı Değerlendirmesi</h2>
      
      <div className="test-tabs">
        <button className={`test-tab ${activeTab === 'entry' ? 'active' : ''}`} onClick={() => setActiveTab('entry')}>
          ➕ Yeni Sınav Girişi
        </button>
        <button className={`test-tab ${activeTab === 'history' ? 'active' : ''}`} onClick={() => setActiveTab('history')}>
          📋 Sınav Geçmişi ({savedExams.length})
        </button>
      </div>

      {activeTab === 'entry' && (
        <div className="test-entry">
          <div className="test-header">
            <div className="header-inputs">
              <div>
                <label>Tarih:</label>
                <input 
                  type="date" 
                  value={examData.date}
                  onChange={(e) => setExamData(prev => ({ ...prev, date: e.target.value }))}
                />
              </div>
              <div>
                <label>Hikaye No:</label>
                <input 
                  type="number" 
                  min="1" 
                  max="50" 
                  value={examData.weekNumber}
                  onChange={(e) => setExamData(prev => ({ ...prev, weekNumber: e.target.value }))}
                  placeholder="1-50"
                />
              </div>
            </div>
            
            <div className="test-format-info">
              <h4>Sınav Formatı:</h4>
              <p><strong>10 soru, 100 puan</strong> - Toplam puanı girin</p>
              <p><strong>Ekonomi:</strong> Puan ÷ 10 = Para | 90-100: +5₺ | 80-89: +3₺</p>
            </div>
          </div>

          <div className="test-table">
            <table>
              <thead>
                <tr>
                  <th>Öğrenci</th>
                  <th>Puan (0-100)</th>
                  <th>Kazanç</th>
                </tr>
              </thead>
              <tbody>
                {examData.studentScores.map(score => {
                  const student = students.find(s => s.id === score.studentId);
                  const economyPoints = calculateEconomyPoints(score.score);
                  return (
                    <tr key={score.studentId}>
                      <td className="student-name">{student?.name}</td>
                      <td>
                        <input 
                          type="number" 
                          min="0" 
                          max="100" 
                          value={score.score}
                          onChange={(e) => updateStudentScore(score.studentId, e.target.value)}
                          onFocus={(e) => e.target.select()}
                          className="total-score-input"
                          inputMode="numeric"
                          placeholder="0-100"
                        />
                      </td>
                      <td className="economy-points">{economyPoints}₺</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          <button className="save-test-btn" onClick={saveStoryExam}>
            💾 Sınavı Kaydet ve Ekonomiye Ekle
          </button>
        </div>
      )}

      {activeTab === 'history' && (
        <div className="test-history">
          {savedExams.length === 0 ? (
            <div className="no-tests">
              <p>Henüz hikaye sınavı kaydı bulunmuyor.</p>
            </div>
          ) : (
            <div className="tests-list">
              {[...savedExams].reverse().map(exam => (
                <div key={exam.id} className="test-record">
                  <div className="test-info">
                    <h4>Hikaye {exam.weekNumber} - {new Date(exam.date).toLocaleDateString('tr-TR')}</h4>
                    <p>Toplam Öğrenci: {exam.studentScores.length}</p>
                  </div>
                  <div className="test-stats">
                    <div className="stat">
                      <span>Ortalama:</span>
                      <span>{(exam.studentScores.reduce((sum, s) => sum + s.score, 0) / exam.studentScores.length).toFixed(1)}/100</span>
                    </div>
                    <div className="stat">
                      <span>En Yüksek:</span>
                      <span>{Math.max(...exam.studentScores.map(s => s.score))}/100</span>
                    </div>
                    <div className="stat">
                      <span>Toplam Puan:</span>
                      <span>{exam.studentScores.reduce((sum, s) => sum + s.economyPoints, 0)}₺</span>
                    </div>
                  </div>
                  <button className="edit-test-btn" onClick={() => setEditingExam(exam)}>✏️ Düzenle</button>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
      
      {editingExam && (
        <div className="edit-modal">
          <div className="edit-modal-content">
            <div className="edit-header">
              <h3>Sınav Düzenleme</h3>
              <button className="close-btn" onClick={() => setEditingExam(null)}>✕</button>
            </div>
            
            <div className="edit-info">
              <p><strong>Tarih:</strong> {new Date(editingExam.date).toLocaleDateString('tr-TR')}</p>
              <p><strong>Hikaye:</strong> {editingExam.weekNumber}</p>
            </div>
            
            <div className="edit-table">
              <table>
                <thead>
                  <tr>
                    <th>Öğrenci</th>
                    <th>Puan (0-100)</th>
                    <th>Kazanç</th>
                    <th>Durum</th>
                  </tr>
                </thead>
                <tbody>
                  {students.map(student => {
                    const existingScore = editingExam.studentScores.find(s => s.studentId === student.id) || {
                      studentId: student.id,
                      score: 0,
                      economyPoints: 0
                    };
                    
                    return (
                      <tr key={student.id}>
                        <td className="student-name">{student.name}</td>
                        <td>
                          <input 
                            type="number" 
                            min="0" 
                            max="100" 
                            defaultValue={existingScore.score}
                            onChange={(e) => {
                              const value = parseInt(e.target.value) || 0;
                              existingScore.score = Math.max(0, Math.min(100, value));
                            }}
                            onFocus={(e) => e.target.select()}
                            className="total-score-input"
                            inputMode="numeric"
                            placeholder="0-100"
                          />
                        </td>
                        <td className="economy-points">{calculateEconomyPoints(existingScore.score)}₺</td>
                        <td className="status">{existingScore.score > 0 ? '✅ Tamamlandı' : '⏳ Bekliyor'}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            
            <div className="edit-actions">
              <button className="cancel-btn" onClick={() => setEditingExam(null)}>İptal</button>
              <button className="save-btn" onClick={() => {
                const updatedScores = students.map(student => {
                  const existingScore = editingExam.studentScores.find(s => s.studentId === student.id) || {
                    studentId: student.id,
                    score: 0
                  };
                  
                  const score = existingScore.score;
                  const economyPoints = calculateEconomyPoints(score);
                  
                  return { ...existingScore, score, economyPoints };
                });
                
                updateStoryExam(editingExam.id, updatedScores);
              }}>💾 Kaydet</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// Weekly Test View Component
function WeeklyTestView({ students }) {
  const [activeTab, setActiveTab] = useState('entry');
  const [editingTest, setEditingTest] = useState(null);
  const [testData, setTestData] = useState({
    date: new Date().toISOString().split('T')[0],
    weekNumber: '',
    studentScores: students.map(student => ({
      studentId: student.id,
      totalCorrect: 0
    }))
  });

  const calculateEconomyPoints = (totalCorrect) => {
    let points = totalCorrect;
    if (totalCorrect >= 14) {
      points += 10;
    } else if (totalCorrect >= 12) {
      points += 5;
    }
    return points;
  };

  const updateStudentScore = (studentId, value) => {
    setTestData(prev => ({
      ...prev,
      studentScores: prev.studentScores.map(score => 
        score.studentId === studentId 
          ? { ...score, totalCorrect: Math.max(0, Math.min(15, parseInt(value) || 0)) }
          : score
      )
    }));
  };

  const getStudentTotals = (studentScore) => {
    const totalCorrect = studentScore.totalCorrect;
    const percentage = ((totalCorrect / 15) * 100).toFixed(1);
    const economyPoints = calculateEconomyPoints(totalCorrect);
    return { totalCorrect, percentage, economyPoints };
  };

  const saveWeeklyTest = () => {
    console.log('🧪 saveWeeklyTest çağrıldı');
    console.log('📊 Test verisi:', testData);
    
    if (!testData.weekNumber) {
      alert('Hafta numarası giriniz!');
      return;
    }

    // Validate that at least one student has scores
    const hasValidScores = testData.studentScores.some(score => 
      score.totalCorrect > 0
    );
    
    if (!hasValidScores) {
      alert('En az bir öğrenci için puan giriniz!');
      return;
    }

    try {
      console.log('✅ Validasyon geçti, test kaydediliyor...');
      
      const processedTestData = {
        id: Date.now(),
        date: testData.date,
        weekNumber: parseInt(testData.weekNumber),
        studentScores: testData.studentScores.map(score => {
          const totals = getStudentTotals(score);
          return { ...score, ...totals };
        })
      };

      console.log('📝 İşlenmiş test verisi:', processedTestData);

      // Save test data with error handling
      const tests = JSON.parse(localStorage.getItem('weeklyTests') || '[]');
      tests.push(processedTestData);
      localStorage.setItem('weeklyTests', JSON.stringify(tests));
      setSavedTests(tests); // State'i anında güncelle
      console.log('💾 Test localStorage\'a kaydedildi');

      // Add points to economy only for students with results
      const currentEconomy = JSON.parse(localStorage.getItem('economy') || '{}');
      processedTestData.studentScores.filter(score => score.totalCorrect > 0).forEach(score => {
        const current = currentEconomy[`earnings-${score.studentId}`] || 0;
        currentEconomy[`earnings-${score.studentId}`] = current + score.economyPoints;
        console.log(`💰 Öğrenci ${score.studentId}: +${score.economyPoints}₺ eklendi`);
      });
      localStorage.setItem('economy', JSON.stringify(currentEconomy));
      
      // State'i de güncelle (sayfa yenileme olmadan)
      window.dispatchEvent(new Event('economyUpdated'));
      console.log('💰 Ekonomi puanları güncellendi');

      alert('✅ Test başarıyla kaydedildi ve ekonomi puanları eklendi!');
      
      // Reset form
      setTestData({
        date: new Date().toISOString().split('T')[0],
        weekNumber: '',
        studentScores: students.map(student => ({
          studentId: student.id,
          totalCorrect: 0
        }))
      });
      console.log('🔄 Form sıfırlandı');
    } catch (error) {
      console.error('❌ Test kaydetme hatası:', error);
      alert('❌ Test kaydedilirken hata oluştu: ' + error.message);
    }
  };

  const updateWeeklyTest = (testId, updatedStudentScores) => {
    try {
      let tests = JSON.parse(localStorage.getItem('weeklyTests') || '[]');
      const testIndex = tests.findIndex(t => t.id === testId);
      
      if (testIndex === -1) return;
      
      const oldScores = tests[testIndex].studentScores;
      const newScores = updatedStudentScores;
      const currentEconomy = JSON.parse(localStorage.getItem('economy') || '{}');
      
      // Her öğrenci için puan farkını hesapla ve ekonomiye uygula
      newScores.forEach(newScore => {
        const oldScore = oldScores.find(old => old.studentId === newScore.studentId);
        const oldPoints = oldScore?.economyPoints || 0;
        const newPoints = newScore.economyPoints;
        const pointDifference = newPoints - oldPoints;
        
        // Sadece değişiklik varsa güncelle
        if (pointDifference !== 0) {
          const current = currentEconomy[`earnings-${newScore.studentId}`] || 0;
          currentEconomy[`earnings-${newScore.studentId}`] = current + pointDifference;
        }
      });
      
      localStorage.setItem('economy', JSON.stringify(currentEconomy));
      window.dispatchEvent(new Event('economyUpdated'));
      
      tests[testIndex].studentScores = newScores;
      localStorage.setItem('weeklyTests', JSON.stringify(tests));
      setSavedTests(tests);
      
      alert('Test başarıyla güncellendi ve ekonomi puanları düzeltildi!');
      setEditingTest(null);
    } catch (error) {
      console.error('Error updating test:', error);
      alert('Test güncellenirken hata oluştu!');
    }
  };

  const [savedTests, setSavedTests] = useState([]);
  
  // Test geçmişini yükle
  useEffect(() => {
    try {
      const tests = JSON.parse(localStorage.getItem('weeklyTests') || '[]');
      setSavedTests(tests);
    } catch (error) {
      console.error('Error loading tests:', error);
      setSavedTests([]);
    }
  }, []);

  return (
    <div className="weekly-test-view">
      <h2>📝 Haftalık Test Değerlendirmesi</h2>
      
      <div className="test-tabs">
        <button className={`test-tab ${activeTab === 'entry' ? 'active' : ''}`} onClick={() => setActiveTab('entry')}>
          ➕ Yeni Test Girişi
        </button>
        <button className={`test-tab ${activeTab === 'history' ? 'active' : ''}`} onClick={() => setActiveTab('history')}>
          📋 Test Geçmişi ({savedTests.length})
        </button>
      </div>

      {activeTab === 'entry' && (
        <div className="test-entry">
          <div className="test-header">
            <div className="header-inputs">
              <div>
                <label>Tarih:</label>
                <input 
                  type="date" 
                  value={testData.date}
                  onChange={(e) => setTestData(prev => ({ ...prev, date: e.target.value }))}
                />
              </div>
              <div>
                <label>Hafta No:</label>
                <input 
                  type="number" 
                  min="1" 
                  max="36" 
                  value={testData.weekNumber}
                  onChange={(e) => setTestData(prev => ({ ...prev, weekNumber: e.target.value }))}
                  placeholder="1-36"
                />
              </div>
            </div>
            
            <div className="test-format-info">
              <h4>Test Formatı:</h4>
              <p><strong>15 soru</strong> toplam - Sadece doğru sayısını girin</p>
              <p><strong>Ekonomi:</strong> 1 doğru = 1₺ | 14-15 doğru = +10₺ | 12-13 doğru = +5₺</p>
            </div>
          </div>

          <div className="test-table">
            <table>
              <thead>
                <tr>
                  <th>Öğrenci</th>
                  <th>Doğru Sayısı (0-15)</th>
                  <th>%</th>
                  <th>Puan</th>
                </tr>
              </thead>
              <tbody>
                {testData.studentScores.map(score => {
                  const student = students.find(s => s.id === score.studentId);
                  const totals = getStudentTotals(score);
                  return (
                    <tr key={score.studentId}>
                      <td className="student-name">{student?.name}</td>
                      <td>
                        <input 
                          type="number" 
                          min="0" 
                          max="15" 
                          value={score.totalCorrect}
                          onChange={(e) => updateStudentScore(score.studentId, e.target.value)}
                          onFocus={(e) => e.target.select()}
                          className="total-score-input"
                          inputMode="numeric"
                          pattern="[0-9]*"
                          placeholder="0-15"
                        />
                      </td>
                      <td className="percentage">{totals.percentage}%</td>
                      <td className="economy-points">{totals.economyPoints}₺</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          <button 
            className="save-test-btn" 
            onClick={(e) => {
              e.preventDefault();
              console.log('🖱️ Testi Kaydet butonuna tıklandı');
              saveWeeklyTest();
            }}
            type="button"
          >
            💾 Testi Kaydet ve Ekonomiye Ekle
          </button>
        </div>
      )}

      {activeTab === 'history' && (
        <div className="test-history">
          {savedTests.length === 0 ? (
            <div className="no-tests">
              <p>Henüz test kaydı bulunmuyor.</p>
            </div>
          ) : (
            <div className="tests-list">
              {[...savedTests].reverse().map(test => (
                <div key={test.id} className="test-record">
                  <div className="test-info">
                    <h4>Hafta {test.weekNumber} - {new Date(test.date).toLocaleDateString('tr-TR')}</h4>
                    <p>Toplam Öğrenci: {test.studentScores.length}</p>
                  </div>
                  <div className="test-stats">
                    <div className="stat">
                      <span>Ortalama:</span>
                      <span>{(test.studentScores.reduce((sum, s) => sum + s.totalCorrect, 0) / test.studentScores.length).toFixed(1)}/15</span>
                    </div>
                    <div className="stat">
                      <span>En Yüksek:</span>
                      <span>{Math.max(...test.studentScores.map(s => s.totalCorrect))}/15</span>
                    </div>
                    <div className="stat">
                      <span>Toplam Puan:</span>
                      <span>{test.studentScores.reduce((sum, s) => sum + s.economyPoints, 0)}₺</span>
                    </div>
                  </div>
                  <button className="edit-test-btn" onClick={() => setEditingTest(test)}>
                    ✏️ Düzenle
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
      
      {editingTest && (
        <div className="edit-modal">
          <div className="edit-modal-content">
            <div className="edit-header">
              <h3>Test Düzenleme</h3>
              <button className="close-btn" onClick={() => setEditingTest(null)}>✕</button>
            </div>
            
            <div className="edit-info">
              <p><strong>Tarih:</strong> {new Date(editingTest.date).toLocaleDateString('tr-TR')}</p>
              <p><strong>Hafta:</strong> {editingTest.weekNumber}</p>
            </div>
            
            <div className="edit-table">
              <table>
                <thead>
                  <tr>
                    <th>Öğrenci</th>
                    <th>Doğru Sayısı (0-15)</th>
                    <th>%</th>
                    <th>Puan</th>
                    <th>Durum</th>
                  </tr>
                </thead>
                <tbody>
                  {students.map(student => {
                    const existingScore = editingTest.studentScores.find(s => s.studentId === student.id) || {
                      studentId: student.id,
                      totalCorrect: 0,
                      economyPoints: 0
                    };
                    
                    return (
                      <tr key={student.id}>
                        <td className="student-name">{student.name}</td>
                        <td>
                          <input 
                            type="number" 
                            min="0" 
                            max="15" 
                            defaultValue={existingScore.totalCorrect}
                            onChange={(e) => {
                              const value = parseInt(e.target.value) || 0;
                              existingScore.totalCorrect = Math.max(0, Math.min(15, value));
                            }}
                            className="total-score-input"
                            inputMode="numeric"
                            pattern="[0-9]*"
                            placeholder="0-15"
                          />
                        </td>
                        <td className="percentage">
                          {((existingScore.totalCorrect / 15) * 100).toFixed(1)}%
                        </td>
                        <td className="economy-points">
                          {calculateEconomyPoints(existingScore.totalCorrect)}₺
                        </td>
                        <td className="status">
                          {existingScore.totalCorrect > 0 ? '✅ Tamamlandı' : '⏳ Bekliyor'}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            
            <div className="edit-actions">
              <button className="cancel-btn" onClick={() => setEditingTest(null)}>
                İptal
              </button>
              <button className="save-btn" onClick={() => {
                const updatedScores = students.map(student => {
                  const existingScore = editingTest.studentScores.find(s => s.studentId === student.id) || {
                    studentId: student.id,
                    totalCorrect: 0
                  };
                  
                  const totalCorrect = existingScore.totalCorrect;
                  const percentage = ((totalCorrect / 15) * 100).toFixed(1);
                  const economyPoints = calculateEconomyPoints(totalCorrect);
                  
                  return {
                    ...existingScore,
                    totalCorrect,
                    percentage,
                    economyPoints
                  };
                });
                
                updateWeeklyTest(editingTest.id, updatedScores);
              }}>
                💾 Kaydet
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// Reports View Component
function ReportsView({ students, behaviors, behaviorIssues, economy }) {
  const [reportType, setReportType] = useState('overview');
  const [showTotalViolations, setShowTotalViolations] = useState(false);
  const today = new Date().toISOString().split('T')[0];
  
  // Analytics hesaplamaları
  const getAnalytics = () => {
    const analytics = {
      totalStudents: students.length,
      totalBehaviorRecords: Object.keys(behaviors).length,
      totalEconomyRecords: Object.keys(economy).length
    };
    
    // Akademik ortalama - devre dışı
    analytics.classAverage = 0;
    
    // Davranış istatistikleri (tüm davranışlar dahil)
    let totalBehaviorIssues = 0;
    Object.values(behaviors).forEach(count => totalBehaviorIssues += count);
    analytics.totalBehaviorIssues = totalBehaviorIssues;
    analytics.behaviorAverage = students.length > 0 ? (totalBehaviorIssues / students.length).toFixed(1) : '0.0';
    
    // Ekonomi istatistikleri
    let totalEarnings = 0, totalSpending = 0;
    students.forEach(student => {
      totalEarnings += economy[`earnings-${student.id}`] || 0;
      totalSpending += economy[`spending-${student.id}`] || 0;
    });
    analytics.totalEarnings = totalEarnings;
    analytics.totalSpending = totalSpending;
    analytics.averageBalance = students.length > 0 ? ((totalEarnings - totalSpending) / students.length).toFixed(1) : '0.0';
    
    return analytics;
  };
  
  const getTopPerformers = useMemo(() => {
    try {
      const customBehaviors = JSON.parse(localStorage.getItem('customBehaviors') || '[]');
      const allBehaviors = [...behaviorIssues, ...customBehaviors.filter(cb => !behaviorIssues.find(bi => bi.code === cb.code))];
      
      return students.map(student => {
        // Akademik puan hesaplama - devre dışı
        let academicPoints = 0;
        
        // Davranış ceza hesaplama (EconomyView ile aynı - tüm geçmiş)
        let behaviorPenalty = 0;
        
        Object.keys(behaviors).forEach(key => {
          if (key.startsWith(`${student.id}-`)) {
            const parts = key.split('-');
            const behaviorCode = parts[1];
            const count = behaviors[key] || 0;
            
            const behavior = allBehaviors.find(b => b.code === behaviorCode);
            if (behavior && count > 0) {
              let penalty = 0;
              switch(behavior.code) {
                case 'D01': penalty = count * 20; break;
                case 'D02': penalty = count * 20; break;
                case 'D03': penalty = count * 5; break;
                default: 
                  const customBehavior = customBehaviors.find(cb => cb.code === behavior.code);
                  penalty = count * (customBehavior?.penalty || 3);
                  break;
              }
              behaviorPenalty += penalty;
            }
          }
        });
        
        // Öğretmen ödülü
        const teacherReward = economy[`earnings-${student.id}`] || 0;
        
        // Toplam para (EconomyView ile aynı)
        const totalMoney = academicPoints + teacherReward - behaviorPenalty;
        
        // Bugünkü davranış sayısı (görüntü için)
        let todayBehaviorCount = 0;
        allBehaviors.forEach(behavior => {
          const key = `${student.id}-${behavior.code}-${today}`;
          todayBehaviorCount += behaviors[key] || 0;
        });
        
        return {
          ...student,
          average: '0.00',
          behaviorCount: todayBehaviorCount,
          totalMoney,
          score: totalMoney // Sıralama için net para kullan
        };
      }).sort((a, b) => b.totalMoney - a.totalMoney);
    } catch (error) {
      console.error('Error calculating top performers:', error);
      return [];
    }
  }, [students, behaviors, economy, behaviorIssues, today]);
  
  const getBehaviorAnalysis = useMemo(() => {
    try {
      const todayAnalysis = {};
      const totalAnalysis = {};
      
      // Default davranışları ekle
      behaviorIssues.forEach(behavior => {
        todayAnalysis[behavior.code] = {
          description: behavior.description,
          count: 0,
          students: []
        };
        totalAnalysis[behavior.code] = {
          description: behavior.description,
          count: 0,
          students: []
        };
      });
      
      // Custom davranışları ekle
      const customBehaviors = JSON.parse(localStorage.getItem('customBehaviors') || '[]');
      customBehaviors.forEach(behavior => {
        if (!todayAnalysis[behavior.code]) {
          todayAnalysis[behavior.code] = {
            description: behavior.description,
            count: 0,
            students: []
          };
          totalAnalysis[behavior.code] = {
            description: behavior.description,
            count: 0,
            students: []
          };
        }
      });
      
      // Tüm davranışları analiz et
      const allBehaviors = [...behaviorIssues, ...customBehaviors.filter(cb => !behaviorIssues.find(bi => bi.code === cb.code))];
      
      students.forEach(student => {
        allBehaviors.forEach(behavior => {
          // Bugünkü davranışlar
          const todayKey = `${student.id}-${behavior.code}-${today}`;
          const todayCount = behaviors[todayKey] || 0;
          if (todayCount > 0) {
            todayAnalysis[behavior.code].count += todayCount;
            todayAnalysis[behavior.code].students.push({ name: student.name, count: todayCount });
          }
          
          // Tüm davranışlar (tüm tarihler)
          Object.keys(behaviors).forEach(key => {
            if (key.startsWith(`${student.id}-${behavior.code}-`)) {
              const count = behaviors[key] || 0;
              if (count > 0) {
                const existingStudent = totalAnalysis[behavior.code].students.find(s => s.name === student.name);
                if (existingStudent) {
                  existingStudent.count += count;
                } else {
                  totalAnalysis[behavior.code].students.push({ name: student.name, count });
                }
                totalAnalysis[behavior.code].count += count;
              }
            }
          });
        });
      });
      
      return {
        today: Object.entries(todayAnalysis)
          .filter(([_, data]) => data.count > 0)
          .sort(([_, a], [__, b]) => b.count - a.count),
        total: Object.entries(totalAnalysis)
          .filter(([_, data]) => data.count > 0)
          .sort(([_, a], [__, b]) => b.count - a.count)
      };
    } catch (error) {
      console.error('Error analyzing behaviors:', error);
      return { today: [], total: [] };
    }
  }, [students, behaviors, behaviorIssues, today]);
  
  const analytics = getAnalytics();
  const topPerformers = getTopPerformers();
  const behaviorAnalysis = getBehaviorAnalysis();
  
  return (
    <div className="reports-view">
      <h2>📈 Gelişmiş Raporlama</h2>
      
      <div className="report-tabs">
        <button className={`report-tab ${reportType === 'overview' ? 'active' : ''}`} onClick={() => setReportType('overview')}>
          📈 Genel Bakış
        </button>
        <button className={`report-tab ${reportType === 'behavior' ? 'active' : ''}`} onClick={() => setReportType('behavior')}>
          ⭐ Davranış Analizi
        </button>
        <button className={`report-tab ${reportType === 'economy' ? 'active' : ''}`} onClick={() => setReportType('economy')}>
          💰 Ekonomi Analizi
        </button>
      </div>
      
      {reportType === 'overview' && (
        <div className="report-section">
          <h3>📈 Sınıf Genel Durumu</h3>
          
          <div className="analytics-grid">
            <div className="analytics-card">
              <h4>👥 Öğrenci Sayısı</h4>
              <div className="big-number">{analytics.totalStudents}</div>
            </div>
            <div className="analytics-card">
              <h4>📚 Sınıf Ortalaması</h4>
              <div className="big-number">{analytics.classAverage}/4</div>
            </div>
            <div className="analytics-card">
              <h4>⭐ Davranış Ortalaması</h4>
              <div className="big-number">{analytics.behaviorAverage}</div>
            </div>
            <div className="analytics-card">
              <h4>💰 Ortalama Bakiye</h4>
              <div className="big-number">{analytics.averageBalance}₺</div>
            </div>
          </div>
          
          <div className="top-performers">
            <h4>🏆 Top 10 - En Başarılı Öğrenciler</h4>
            <div className="performers-list">
              {topPerformers.slice(0, 10).map((student, index) => {
                const rankEmoji = index === 0 ? '🥇' : index === 1 ? '🥈' : index === 2 ? '🥉' : '🏅';
                const rankClass = index < 3 ? 'podium' : 'regular';
                
                return (
                  <div key={student.id} className={`performer-item ${rankClass}`}>
                    <span className="rank">{rankEmoji} #{index + 1}</span>
                    <span className="name">{student.name}</span>
                    <span className="stats">
                      Ort: {student.average} | Davranış: {student.behaviorCount} | Para: {student.totalMoney}₺
                    </span>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}
      
      {reportType === 'behavior' && (
        <div className="report-section">
          <h3>⭐ Davranış Analizi</h3>
          
          <div className="behavior-stats">
            <div className="behavior-summary">
              <div className="summary-card">
                <h4>Toplam İhlal</h4>
                <div className="big-number">{analytics.totalBehaviorIssues}</div>
              </div>
              <div className="summary-card">
                <h4>Temiz Öğrenci</h4>
                <div className="big-number">
                  {students.filter(student => {
                    const customBehaviors = JSON.parse(localStorage.getItem('customBehaviors') || '[]');
                    const allBehaviors = [...behaviorIssues, ...customBehaviors.filter(cb => !behaviorIssues.find(bi => bi.code === cb.code))];
                    return allBehaviors.every(behavior => {
                      const key = `${student.id}-${behavior.code}-${today}`;
                      return (behaviors[key] || 0) === 0;
                    });
                  }).length}
                </div>
              </div>
            </div>
            
            <div className="behavior-breakdown">
              <h4>📅 Bugünkü İhlaller</h4>
              {behaviorAnalysis.today.length > 0 ? (
                behaviorAnalysis.today.map(([code, data]) => (
                  <div key={code} className="behavior-item today">
                    <div className="behavior-header">
                      <span className="behavior-code">{code}</span>
                      <span className="behavior-count">{data.count} kez</span>
                    </div>
                    <p className="behavior-desc">{data.description}</p>
                    <div className="affected-students">
                      {data.students.map(student => (
                        <span key={student.name} className="student-tag">
                          {student.name} ({student.count})
                        </span>
                      ))}
                    </div>
                  </div>
                ))
              ) : (
                <div className="no-violations">
                  <p>🎉 Bugün hiç ihlal yok! Harikasınız Çocuklar!</p>
                </div>
              )}
              
              <div className="total-violations-section" style={{marginTop: '30px'}}>
                <h4 
                  className="collapsible-header" 
                  onClick={() => setShowTotalViolations(!showTotalViolations)}
                  style={{
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '10px',
                    padding: '10px',
                    backgroundColor: '#f8f9fa',
                    borderRadius: '5px',
                    border: '1px solid #dee2e6'
                  }}
                >
                  <span>{showTotalViolations ? '▼' : '▶'}</span>
                  📊 Kuralları İhlal Edenler Tüm Zamanlar - {behaviorAnalysis.total.length} tür
                </h4>
                
                {showTotalViolations && (
                  <div className="total-violations-content" style={{marginTop: '10px'}}>
                    {behaviorAnalysis.total.map(([code, data]) => (
                      <div key={code} className="behavior-item total">
                        <div className="behavior-header">
                          <span className="behavior-code">{code}</span>
                          <span className="behavior-count">{data.count} kez</span>
                        </div>
                        <p className="behavior-desc">{data.description}</p>
                        <div className="affected-students">
                          {data.students.map(student => (
                            <span key={student.name} className="student-tag">
                              {student.name} ({student.count})
                            </span>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
      
      {reportType === 'economy' && (
        <div className="report-section">
          <h3>💰 Ekonomi Analizi</h3>
          
          <div className="economy-stats">
            <div className="economy-summary">
              <div className="summary-card">
                <h4>Toplam Kazanç</h4>
                <div className="big-number">{analytics.totalEarnings}₺</div>
              </div>
              <div className="summary-card">
                <h4>Toplam Harcama</h4>
                <div className="big-number">{analytics.totalSpending}₺</div>
              </div>
              <div className="summary-card">
                <h4>Net Bakiye</h4>
                <div className="big-number">{analytics.totalEarnings - analytics.totalSpending}₺</div>
              </div>
            </div>
            
            <div className="wealth-distribution">
              <h4>Zenginlik Dağılımı</h4>
              <div className="wealth-list">
                {topPerformers.slice(0, 10).map((student, index) => (
                  <div key={student.id} className="wealth-item">
                    <span className="rank">#{index + 1}</span>
                    <span className="name">{student.name}</span>
                    <span className="money">{student.totalMoney}₺</span>
                    <div className="money-bar">
                      <div 
                        className="bar" 
                        style={{ 
                          width: `${Math.max(0, (student.totalMoney / (Math.max(...topPerformers.map(s => s.totalMoney)) || 1)) * 100)}%`,
                          backgroundColor: student.totalMoney >= 0 ? '#27ae60' : '#e74c3c'
                        }}
                      ></div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}



// Export View Component
function ExportView({ students, behaviors, behaviorIssues, economy }) {
  const today = new Date().toISOString().split('T')[0];
  
  const exportToCSV = (data, filename) => {
    if (!data || data.length === 0) {
      alert('Export edilecek veri bulunamadı!');
      return;
    }
    
    try {
      const csvContent = data.map(row => 
        Object.values(row).map(val => {
          let sanitized = String(val).replace(/"/g, '""');
          // Prevent CSV injection (formula injection)
          if (sanitized.match(/^[=+\-@]/)) {
            sanitized = "'" + sanitized;
          }
          return `"${sanitized}"`;
        }).join(',')
      ).join('\n');
      
      const headers = Object.keys(data[0]).map(key => `"${key}"`).join(',');
      const fullContent = headers + '\n' + csvContent;
      
      const blob = new Blob([fullContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      link.href = url;
      link.download = `${filename}_${today}.csv`;
      link.click();
      
      // Immediate cleanup
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error exporting CSV:', error);
      alert('CSV export edilirken hata oluştu!');
    }
  };
  
  const exportStudentList = () => {
    const data = students.map(student => ({
      'Okul No': student.schoolNumber,
      'Ad Soyad': student.name,
      'Veli Adı': student.parentName,
      'Telefon': student.parentPhone,
      'WhatsApp': student.parentWhatsApp
    }));
    exportToCSV(data, 'ogrenci_listesi');
  };
  

  
  const exportBehaviors = () => {
    try {
      const data = [];
      students.forEach(student => {
        behaviorIssues.forEach(behavior => {
          const key = `${student.id}-${behavior.code}-${today}`;
          const count = behaviors[key] || 0;
          if (count > 0) {
            data.push({
              'Okul No': student.schoolNumber,
              'Ad Soyad': student.name,
              'Davranış Kodu': behavior.code,
              'Açıklama': behavior.description,
              'Sayı': count,
              'Tarih': today
            });
          }
        });
      });
      exportToCSV(data, 'davranis_kayitlari');
    } catch (error) {
      console.error('Error exporting behaviors:', error);
      alert('Davranış verileri export edilirken hata oluştu!');
    }
  };
  
  const exportEconomy = () => {
    try {
      const data = students.map(student => {
        const earnings = economy[`earnings-${student.id}`] || 0;
        const spending = economy[`spending-${student.id}`] || 0;
        let penalty = 0;
        behaviorIssues.forEach(behavior => {
          const key = `${student.id}-${behavior.code}-${today}`;
          penalty += (behaviors[key] || 0) * 3;
        });
        const totalMoney = earnings - penalty;
        
        return {
          'Okul No': student.schoolNumber,
          'Ad Soyad': student.name,
          'Kazanç': earnings,
          'Ceza': penalty,
          'Genel Para': totalMoney,
          'Harcama': spending,
          'Bakiye': totalMoney - spending
        };
      });
      exportToCSV(data, 'sinif_ekonomisi');
    } catch (error) {
      console.error('Error exporting economy:', error);
      alert('Ekonomi verileri export edilirken hata oluştu!');
    }
  };
  
  const exportFullReport = () => {
    try {
      const data = students.map(student => {
        // Akademik ortalama - devre dışı
        const average = 0;
        
        // Davranış sayısı
        let behaviorCount = 0;
        behaviorIssues.forEach(behavior => {
          const key = `${student.id}-${behavior.code}-${today}`;
          behaviorCount += behaviors[key] || 0;
        });
        
        // Ekonomi
        const earnings = economy[`earnings-${student.id}`] || 0;
        const penalty = behaviorCount * 3;
        const totalMoney = earnings - penalty;
        
        return {
          'Okul No': student.schoolNumber,
          'Ad Soyad': student.name,
          'Veli': student.parentName,
          'Telefon': student.parentPhone,

          'Davranış Sayısı': behaviorCount,
          'Kazanç': earnings,
          'Ceza': penalty,
          'Genel Para': totalMoney,
          'Tarih': today
        };
      });
      exportToCSV(data, 'tam_rapor');
    } catch (error) {
      console.error('Error exporting full report:', error);
      alert('Tam rapor export edilirken hata oluştu!');
    }
  };
  
  return (
    <div className="export-view">
      <h2>📄 Veri Export İşlemleri</h2>
      
      <div className="export-cards">
        <div className="export-card">
          <div className="card-header">
            <h3>👥 Öğrenci Listesi</h3>
            <p>Tüm öğrenci bilgileri ve veli iletişim bilgileri</p>
          </div>
          <button className="export-btn" onClick={exportStudentList}>
            📄 Excel İndir
          </button>
        </div>
        

        
        <div className="export-card">
          <div className="card-header">
            <h3>⭐ Davranış Kayıtları</h3>
            <p>Günlük davranış kayıtları ve ihlal sayıları</p>
          </div>
          <button className="export-btn" onClick={exportBehaviors}>
            📄 Excel İndir
          </button>
        </div>
        
        <div className="export-card">
          <div className="card-header">
            <h3>💰 Sınıf Ekonomisi</h3>
            <p>Para puanları, kazanç, ceza ve harcama bilgileri</p>
          </div>
          <button className="export-btn" onClick={exportEconomy}>
            📄 Excel İndir
          </button>
        </div>
        
        <div className="export-card featured">
          <div className="card-header">
            <h3>📈 Tam Rapor</h3>
            <p>Tüm verileri içeren kapsamlı rapor</p>
          </div>
          <button className="export-btn primary" onClick={exportFullReport}>
            🚀 Tam Rapor İndir
          </button>
        </div>
      </div>
      
      <div className="export-info">
        <h3>📝 Bilgi</h3>
        <ul>
          <li>Tüm dosyalar Excel uyumlu CSV formatında indirilir</li>
          <li>Dosya adlarına otomatik tarih eklenir</li>
          <li>Türkçe karakterler desteklenir</li>
          <li>Veriler güncel tarih itibariyle export edilir</li>
        </ul>
      </div>
    </div>
  );
}

// Telegram View Component
function TelegramView({ students, behaviors, behaviorIssues }) {
  const [kvStudents, setKvStudents] = useState([]);
  const [selectedStudents, setSelectedStudents] = useState([]);
  const [messageType, setMessageType] = useState('daily_behavior');
  const [customMessage, setCustomMessage] = useState('');
  const [bookName, setBookName] = useState('');
  const [startDate, setStartDate] = useState(new Date().toISOString().split('T')[0]);
  const [endDate, setEndDate] = useState('');
  const today = new Date().toISOString().split('T')[0];
  

  const toggleStudent = (studentId) => {
    setSelectedStudents(prev => 
      prev.includes(studentId) 
        ? prev.filter(id => id !== studentId)
        : [...prev, studentId]
    );
  };
  
  const selectAll = () => {
    setSelectedStudents(students.map(s => s.id));
  };
  
  const clearAll = () => {
    setSelectedStudents([]);
  };
  
  const generateBehaviorMessage = (student) => {
    try {
      // Güvenli veri çekme - localStorage'dan güncel veriyi al
      const currentBehaviors = JSON.parse(localStorage.getItem('behaviors') || '{}');
      const customBehaviors = JSON.parse(localStorage.getItem('customBehaviors') || '[]');
      const allBehaviorTypes = [...behaviorIssues, ...customBehaviors.filter(cb => !behaviorIssues.find(bi => bi.code === cb.code))];
      
      const issues = allBehaviorTypes.filter(behavior => {
        const key = `${student.id}-${behavior.code}-${today}`;
        const count = currentBehaviors[key] || 0;
        return typeof count === 'number' && count > 0;
      });
      
      if (issues.length === 0) {
        // XSS koruması: student.parentName ve student.name'i sanitize et
        const safeName = String(student.name).replace(/[<>"'&]/g, '');
        const safeParentName = String(student.parentName).replace(/[<>"'&]/g, '');
        return `Sayın ${safeParentName}, ${safeName} bugün örnek davranışlar sergiledi. Tebrikler! 🎆`;
      }
      
      const issueList = issues.map(issue => {
        const key = `${student.id}-${issue.code}-${today}`;
        const count = currentBehaviors[key] || 0;
        const safeDescription = String(issue.description).replace(/[<>"'&]/g, '');
        return `${safeDescription} (${count} kez)`;
      }).join(', ');
      
      const safeName = String(student.name).replace(/[<>"'&]/g, '');
      const safeParentName = String(student.parentName).replace(/[<>"'&]/g, '');
      return `Sayın ${safeParentName}, ${safeName} bugün şu davranış sorunları yaşadı: ${issueList}. Lütfen gerekli ikazları yapınız.`;
    } catch (error) {
      console.error('Error generating behavior message:', error);
      return 'Mesaj oluşturulurken hata oluştu.';
    }
  };
  
  // Kazanım bazlı tavsiye sistemi
  const getAchievementAdvice = (achievementCode, grade) => {
    if (grade >= 4) return '';
    
    const adviceMap = {
      'M.3.1.1.2': `🔢 *Sayma Çalışması Gerekli*\n\n*Evde yapın:*\n- Merdiven çıkarken sayma (1,2,3...)\n- 10'ar 10'ar sayma oyunu\n- Müzikle birlikte sayma\n- Günlük 10 dakika sayma alıştırması\n\n*Sabırlı olun, her gün biraz daha iyi olacak.*`,
      
      'M.3.1.1.6': `🎯 *Ritmik Sayma Sorunu Tespit Edildi*\n\n*Önce test edin:*\n- 6'şar sayabilir mi? (6,12,18,24...)\n- 7'şer sayabilir mi? (7,14,21,28...)\n- 8'er sayabilir mi? (8,16,24,32...)\n- 9'ar sayabilir mi? (9,18,27,36...)\n\n*Hangi sayma zorsa ona odaklanın:*\n- Sorunlu olanı günde 5 dakika tekrar edin\n- Parmakla yardım alabilir\n- Ritmik sayma şarkıları dinletin\n\n*Bir tanesini öğrendikten sonra diğerine geçin.*`,
      
      'M.3.1.2.1': `➕ *Toplama İşlemi Çalışması*\n\n*Evde yapın:*\n- Markette basit hesaplar\n- Oyuncak paralarla oyun\n- Fasulye ile toplama\n- Parmak hesabı\n\n*Günlük 15 dakika yeterli.*`
    };
    
    return adviceMap[achievementCode] || '';
  };


  
  const generateAcademicMessage = (student) => {
    const safeName = String(student.name).replace(/[<>"'&]/g, '');
    const safeParentName = String(student.parentName).replace(/[<>"'&]/g, '');
    return `Sayın ${safeParentName}, ${safeName} bu hafta akademik performansı hakkında bilgi verilecektir.`;
  };
  
  const generateAbsenceMessage = (student) => {
    return `Sayın ${student.parentName}, ${student.name} bugün sınıfta değil. Her şey yolundadır umarım, bilginize. 🌸`;
  };
  
  const generateTestMessage = (student) => {
    try {
      const weeklyTests = JSON.parse(localStorage.getItem('weeklyTests') || '[]');
      const latestTest = weeklyTests[weeklyTests.length - 1];
      
      const safeName = String(student.name).replace(/[<>"'&]/g, '');
      const safeParentName = String(student.parentName).replace(/[<>"'&]/g, '');
      
      if (!latestTest) {
        return `Sayın ${safeParentName}, ${safeName} için henüz haftalık test sonucu bulunmuyor.`;
      }
      
      const studentResult = latestTest.studentScores.find(score => score.studentId === student.id);
      
      if (!studentResult) {
        return `Sayın ${safeParentName}, ${safeName} haftalık teste katılmamış.`;
      }
      
      return `Sayın ${safeParentName}, ${safeName} Hafta ${latestTest.weekNumber} testinde ${studentResult.totalCorrect}/15 doğru yaparak %${studentResult.percentage} başarı gösterdi. 🎆`;
    } catch (error) {
      console.error('Error generating test message:', error);
      return 'Test mesajı oluşturulurken hata oluştu.';
    }
  };
  
  const generateStoryExamMessage = (student) => {
    try {
      const storyExams = JSON.parse(localStorage.getItem('storyExams') || '[]');
      const latestExam = storyExams[storyExams.length - 1];
      
      const safeName = String(student.name).replace(/[<>"'&]/g, '');
      const safeParentName = String(student.parentName).replace(/[<>"'&]/g, '');
      
      if (!latestExam) {
        return `Sayın ${safeParentName}, ${safeName} için henüz hikaye sınavı sonucu bulunmuyor.`;
      }
      
      const studentResult = latestExam.studentScores.find(score => score.studentId === student.id);
      
      if (!studentResult || studentResult.score === 0) {
        return `Sayın ${safeParentName}, ${safeName} hikaye sınavına katılmamış.`;
      }
      
      const score = studentResult.score;
      let message = `Sayın ${safeParentName},\n\n${safeName} Hikaye ${latestExam.weekNumber} sınavında 100 üzerinden ${score} puan aldı.\n\n`;
      
      if (score >= 90) {
        message += `🌟 MÜKEMMEL PERFORMANS! 🌟\n${safeName} harika bir başarı gösterdi! Hikaye anlama ve yorumlama becerileri çok gelişmiş. Tebrikler!`;
      } else if (score >= 80) {
        message += `⭐ ÇOK BAŞARILI! ⭐\n${safeName} hikayeyi çok iyi anlamış ve sorulara doğru cevaplar vermiş. Harika!`;
      } else if (score >= 70) {
        message += `👍 BAŞARILI! 👍\n${safeName} hikayeyi iyi anlamış. Biraz daha dikkatli okuma ile daha da başarılı olabilir.\n\n📚 Öneri: Ana karakterleri ve olayların sırasını not almasını öneririm.`;
      } else if (score >= 60) {
        message += `📖 GELİŞMELİ\n${safeName} hikayeyi anlamış ancak bazı detayları kaçırmış.\n\n💡 Öneriler:\n- Ana fikri bulmaya çalışmalı\n- Evde birlikte sesli okuma yapabilirsiniz\n- Hikaye ile ilgili sorular sorun\n- Karakterlerin duygularını konuşun`;
      } else if (score >= 50) {
        message += `⚠️ DESTEK GEREKLİ\n${safeName} hikaye anlama konusunda zorlanmış.\n\n🎯 Öneriler:\n- Her gün 15 dakika sesli okuma yapın\n- Okuduğu metni kendi cümleleriyle anlatmasını isteyin\n- "Kim, ne yaptı, nerede, ne zaman?" sorularını sorun\n- Okul kütüphanesinden seviyesine uygun kitaplar alsın`;
      } else {
        message += `🚨 CİDDİ DESTEK GEREKLİ\n${safeName} hikaye anlama konusunda ciddi zorlanma yaşıyor.\n\n📞 Öneriler:\n- Hikayeyi yavaş ve dikkatli okumalı\n- Her paragraftan sonra "Ne anladım?" sorusunu kendine sormalı\n- Okuduğunu anlama stratejileri üzerinde çalışmalıyız\n- Her gün mutlaka 20 dakika birlikte okuma yapın\n- Resimleri göstererek hikaye anlatın\n- Çocuğunuzun ilgi duyduğu konularda kısa metinler okuyun\n- Her paragrafı okuduktan sonra durup, o bölümü kendi cümleleriyle anlatmasını isteyin\n- "Kim vardı?", "Ne oldu?", "Nerede geçti?" gibi basit sorular sorun\n\nBirlikte çalışarak ${safeName}'nın okuma becerisini geliştirebiliriz.`;
      }
      
      return message;
    } catch (error) {
      console.error('Error generating story exam message:', error);
      return 'Hikaye sınavı mesajı oluşturulurken hata oluştu.';
    }
  };
  
  const generateBookMessage = (student) => {
    const bookText = bookName ? `"${bookName}" kitabı` : 'kitap okuma görevi';
    return `Sayın ${student.parentName}, ${student.name} için ${bookText} verildi. Önümüzdeki hafta kitap değerlendirmesi yapacağız. Planlı bir şekilde günlük ve dikkatli okuma yapması konusunda desteklemenizi rica ederim. 📚✨`;
  };
  
  const generateBirthdayMessage = (student) => {
    return `🎂 Doğum Günü Tebrik Mesajı 🎂\n\nSayın ${student.parentName},\n\nBugün ${student.name}'nın doğum günü vesilesiyle, kendisine sınıfım adına en içten tebriklerimi iletiyorum.\nYeni yaşının; sağlık, huzur, mutluluk ve akademik başarılarla dolu geçmesini dilerim.\n\nŞadiye PATCI\n4/P Sınıf Öğretmeni`;
  };
  

  
  const generateCounselorReport = () => {
    try {
      // Güvenli veri çekme - localStorage'dan güncel veriyi al
      const allBehaviors = JSON.parse(localStorage.getItem('behaviors') || '{}');
      const customBehaviors = JSON.parse(localStorage.getItem('customBehaviors') || '[]');
      const allBehaviorTypes = [...behaviorIssues, ...customBehaviors.filter(cb => !behaviorIssues.find(bi => bi.code === cb.code))];
      
      const today = new Date();
      const weekAgo = new Date(today.getTime() - 7 * 24 * 60 * 60 * 1000);
    
    let totalViolations = 0;
    const problemStudents = [];
    
    students.forEach(student => {
      let studentViolations = 0;
      let seriousIssues = [];
      let repeatedIssues = [];
      
      // Son 7 günü kontrol et
      for (let d = 0; d < 7; d++) {
        const checkDate = new Date(today.getTime() - d * 24 * 60 * 60 * 1000).toISOString().split('T')[0];
        
        allBehaviorTypes.forEach(behavior => {
          const key = `${student.id}-${behavior.code}-${checkDate}`;
          const count = allBehaviors[key] || 0;
          
          // Geçerli sayı kontrolü
          if (typeof count === 'number' && count > 0) {
            studentViolations += count;
            
            if (behavior.code === 'D01' || behavior.code === 'D02') {
              seriousIssues.push(`${behavior.description} (${count})`);
            }
            if (count >= 4) {
              repeatedIssues.push(`${behavior.description} (${count})`);
            }
          }
        });
      }
      
      totalViolations += studentViolations;
      
      if (seriousIssues.length > 0 || repeatedIssues.length > 0) {
        problemStudents.push({
          name: student.name,
          serious: seriousIssues,
          repeated: repeatedIssues,
          total: studentViolations
        });
      }
    });
    
    const reportDate = today.toLocaleDateString('tr-TR');
    let report = `🤖 OTOMATİK SİSTEM RAPORU\n\n📊 HAFTALIK DAVRANIŞSAL RAPOR\n${classInfo.className} - ${reportDate}\n\n`;
    
    report += `📈 GENEL DURUM:\nSon 7 gün: ${totalViolations} toplam ihlal\n`;
    
    if (problemStudents.length === 0) {
      report += `\n🎉 Harika haber! Hiç ciddi davranış sorunu yok.\n`;
    } else {
      report += `\n⚠️ DİKKAT GEREKTİREN ÖĞRENCİLER (${problemStudents.length}):\n`;
      
      // Sıralama: En çok ihlali olan öğrenciler önce
      problemStudents
        .sort((a, b) => b.total - a.total)
        .slice(0, 5)
        .forEach(student => {
          report += `\n• ${student.name} (${student.total} ihlal):\n`;
          if (student.serious.length > 0) {
            report += `  🚨 Ciddi: ${student.serious.join(', ')}\n`;
          }
          if (student.repeated.length > 0) {
            report += `  🔄 Tekrar: ${student.repeated.join(', ')}\n`;
          }
        });
      
      if (problemStudents.length > 5) {
        report += `\n... ve ${problemStudents.length - 5} öğrenci daha\n`;
      }
    }
    
      report += `\n📞 Detay için: ${classInfo.teacher}\n${classInfo.teacherPhone}\n\nℹ️ Bu rapor otomatik olarak her Cuma saat 17:00'de gönderilmektedir.`;
      
      return report;
    } catch (error) {
      console.error('Error generating counselor report:', error);
      return 'Rapor oluşturulurken hata oluştu.';
    }
  };
  
  const getMessage = (student) => {
    switch(messageType) {
      case 'daily_behavior':
        return generateBehaviorMessage(student);
      case 'weekly_academic':
        return generateAcademicMessage(student);
      case 'absence':
        return generateAbsenceMessage(student);
      case 'test_results':
        return generateTestMessage(student);
      case 'story_exam_results':
        return generateStoryExamMessage(student);
      case 'book_notification':
        return generateBookMessage(student);
      case 'counselor_report':
        return generateCounselorReport();
      case 'birthday':
        return generateBirthdayMessage(student);
      case 'custom':
        const safeParentName = String(student.parentName).replace(/[<>"'&]/g, '');
        const safeStudentName = String(student.name).replace(/[<>"'&]/g, '');
        const safeMessage = String(customMessage).replace(/[<>"'&]/g, '');
        return safeMessage.replace('{parentName}', safeParentName).replace('{studentName}', safeStudentName);
      default:
        return '';
    }
  };
  
  const sendMessages = async () => {
    let messages;
    
    if (messageType === 'counselor_report') {
      // Rehber öğretmene özel mesaj
      messages = [{
        chatId: classInfo.counselor.telegram,
        message: generateCounselorReport(),
        studentName: 'Rehber Öğretmen',
        parentName: classInfo.counselor.name
      }];
    } else {
      // Normal veli mesajları
      messages = selectedStudents.map(studentId => {
        const student = students.find(s => s.id === studentId);
        return {
          chatId: student.parentTelegram,
          message: getMessage(student),
          studentName: student.name,
          parentName: student.parentName
        };
      });
    }
    
    try {
      // Import telegramService dynamically
      const { telegramService } = await import('./services/telegramService');
      const results = await telegramService.sendBulkMessages(messages);
      
      const successCount = results.filter(r => r.success).length;
      const failCount = results.length - successCount;
      
      if (failCount === 0) {
        if (messageType === 'counselor_report') {
          alert(`✅ Rehber öğretmene rapor gönderildi!`);
        } else {
          alert(`✅ ${successCount} mesaj başarıyla gönderildi!`);
        }
      } else {
        alert(`⚠️ ${successCount} başarılı, ${failCount} başarısız mesaj`);
      }
      
      setSelectedStudents([]);
    } catch (error) {
      if (process.env.NODE_ENV === 'development') {
        console.error('WhatsApp gönderim hatası:', error);
      }
      alert('❌ Telegram mesaj gönderiminde hata oluştu: ' + error.message);
    }
  };
  
  return (
    <div className="telegram-view">
      <h2>📱 Telegram Mesajlaşma</h2>
      
      <div className="message-controls">
        <div className="message-type-selector">
          <h3>Mesaj Tipi</h3>
          <div className="radio-group">
            <label>
              <input 
                type="radio" 
                value="daily_behavior" 
                checked={messageType === 'daily_behavior'}
                onChange={(e) => setMessageType(e.target.value)}
              />
              📅 Günlük Davranış
            </label>

            <label>
              <input 
                type="radio" 
                value="test_results" 
                checked={messageType === 'test_results'}
                onChange={(e) => setMessageType(e.target.value)}
              />
              📝 Haftalık Test Sonucu
            </label>
            <label>
              <input 
                type="radio" 
                value="story_exam_results" 
                checked={messageType === 'story_exam_results'}
                onChange={(e) => setMessageType(e.target.value)}
              />
              📖 Hikaye Sınavı Sonucu
            </label>
            <label>
              <input 
                type="radio" 
                value="absence" 
                checked={messageType === 'absence'}
                onChange={(e) => setMessageType(e.target.value)}
              />
              🏠 Devamsızlık Bildirimi
            </label>
            <label>
              <input 
                type="radio" 
                value="book_notification" 
                checked={messageType === 'book_notification'}
                onChange={(e) => setMessageType(e.target.value)}
              />
              📚 Kitap Bildirimi
            </label>
            <label>
              <input 
                type="radio" 
                value="counselor_report" 
                checked={messageType === 'counselor_report'}
                onChange={(e) => setMessageType(e.target.value)}
              />
              📊 Rehber Öğretmene Haftalık Rapor
            </label>
            <label>
              <input 
                type="radio" 
                value="birthday" 
                checked={messageType === 'birthday'}
                onChange={(e) => setMessageType(e.target.value)}
              />
              🎂 Bugün Doğum Günü Olanlar
            </label>

            <label>
              <input 
                type="radio" 
                value="custom" 
                checked={messageType === 'custom'}
                onChange={(e) => setMessageType(e.target.value)}
              />
              ✍️ Özel Mesaj
            </label>
          </div>
        </div>
        
        {messageType === 'custom' && (
          <div className="custom-message">
            <h3>Özel Mesaj</h3>
            <textarea
              value={customMessage}
              onChange={(e) => setCustomMessage(e.target.value)}
              placeholder="Mesajınızı yazın... {parentName} ve {studentName} kullanabilirsiniz."
              rows={4}
            />
          </div>
        )}
        
        {messageType === 'book_notification' && (
          <div className="book-notification-form">
            <h3>Kitap Bilgileri</h3>
            <div className="book-form-grid">
              <input
                type="text"
                placeholder="Kitap adı (isteğe bağlı)"
                value={bookName}
                onChange={(e) => setBookName(e.target.value)}
              />
              <div>
                <label>Verilme Tarihi:</label>
                <input
                  type="date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                />
              </div>
              <div>
                <label>Teslim Tarihi:</label>
                <input
                  type="date"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                />
              </div>
            </div>
          </div>
        )}
        
        {messageType === 'birthday' && (() => {
          const { getTodaysBirthdays } = require('./services/birthdayService');
          const todaysBirthdays = getTodaysBirthdays();
          
          return (
            <div className="birthday-students">
              <h3>🎉 Bugün Doğum Günü Olan Öğrenciler</h3>
              <div className="birthday-list">
                {todaysBirthdays.length > 0 ? (
                  todaysBirthdays.map(student => (
                    <div key={student.id} className="birthday-student-card" style={{
                      padding: '15px',
                      margin: '10px 0',
                      background: 'linear-gradient(135deg, #ff6b6b, #ffd93d)',
                      borderRadius: '10px',
                      color: 'white',
                      fontWeight: 'bold'
                    }}>
                      <div style={{ fontSize: '1.5rem' }}>🎂 {student.name}</div>
                      <div style={{ fontSize: '1rem', marginTop: '5px' }}>
                        {new Date().getFullYear() - new Date(student.birthDate).getFullYear()} yaşında
                      </div>
                    </div>
                  ))
                ) : (
                  <p>Bugün doğum günü olan öğrenci bulunmamaktadır.</p>
                )}
              </div>
            </div>
          );
        })()}
        
        {messageType !== 'counselor_report' && messageType !== 'birthday' && (
          <div className="student-selection">
            <div className="selection-controls">
              <button onClick={selectAll}>Hepsini Seç</button>
              <button onClick={clearAll}>Temizle</button>
              <span className="selected-count">{selectedStudents.length} öğrenci seçili</span>
            </div>
            
            <div className="students-list">
              {students.map(student => (
                <div 
                  key={student.id} 
                  className="student-checkbox"
                  onClick={() => toggleStudent(student.id)}
                >
                  <input
                    type="checkbox"
                    checked={selectedStudents.includes(student.id)}
                    onChange={() => {}} // Boş bırak, parent onClick kullan
                    readOnly
                  />
                  <span className="student-info">
                    <strong>{student.name}</strong>
                    <small>
                      {student.parentName} - 
                      {(() => {
                        const chatId = student.parentTelegram || student.telegramChatId;
                        return chatId ? `✅ ${chatId}` : '⏳ Bekliyor';
                      })()}
                    </small>
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}
        
        {messageType === 'counselor_report' && (
          <div className="counselor-info">
            <h3>📊 Rapor Hedefi</h3>
            <div className="counselor-card">
              <p><strong>{classInfo.counselor.name}</strong></p>
              <p>{classInfo.counselor.title}</p>
              <p>📞 {classInfo.counselor.telegram}</p>
              <small>Bu rapor sadece rehber öğretmene gönderilecektir.</small>
            </div>
          </div>
        )}
        
        <div className="message-preview">
          <h3>Önizleme</h3>
          {messageType === 'counselor_report' ? (
            <div className="preview-message">
              {generateCounselorReport()}
            </div>
          ) : messageType === 'birthday' ? (() => {
            const { getTodaysBirthdays } = require('./services/birthdayService');
            const todaysBirthdays = getTodaysBirthdays();
            return (
              <div className="preview-message">
                {todaysBirthdays.length > 0 ? (
                  generateBirthdayMessage(todaysBirthdays[0])
                ) : (
                  'Bugün doğum günü olan öğrenci bulunmamaktadır.'
                )}
              </div>
            );
          })() : selectedStudents.length > 0 && (
            <div className="preview-message">
              {getMessage(students.find(s => s.id === selectedStudents[0]))}
            </div>
          )}
        </div>
        
        {messageType !== 'birthday' ? (
          <button 
            className="send-btn"
            onClick={sendMessages}
            disabled={messageType === 'counselor_report' ? false : (selectedStudents.length === 0 || (messageType === 'custom' && !customMessage.trim()))}
          >
            {messageType === 'counselor_report' 
              ? '📊 Rehber Öğretmene Rapor Gönder'
              : `🚀 ${selectedStudents.length} Mesaj Gönder`
            }
          </button>
        ) : (() => {
          const { getTodaysBirthdays } = require('./services/birthdayService');
          const todaysBirthdays = getTodaysBirthdays();
          
          return todaysBirthdays.length > 0 ? (
            <button 
              className="send-btn"
              onClick={async () => {
                const messages = todaysBirthdays.map(student => ({
                  chatId: student.parentTelegram,
                  message: generateBirthdayMessage(student),
                  studentName: student.name,
                  parentName: student.parentName
                }));
                
                try {
                  const { telegramService } = await import('./services/telegramService');
                  const results = await telegramService.sendBulkMessages(messages);
                  const successCount = results.filter(r => r.success).length;
                  alert(`✅ ${successCount} doğum günü mesajı gönderildi!`);
                } catch (error) {
                  alert('❌ Mesaj gönderiminde hata oluştu: ' + error.message);
                }
              }}
            >
              🎂 {todaysBirthdays.length} Doğum Günü Mesajı Gönder
            </button>
          ) : null;
        })()}
      </div>
    </div>
  );
}

// Economy View Component
function EconomyView({ students, behaviors, behaviorIssues, economy, onUpdateEconomy, setEconomy, setBehaviors }) {
  const [currentTab, setCurrentTab] = useState('overview');
  const [selectedStudents, setSelectedStudents] = useState([]);
  const [submitted, setSubmitted] = useState([]);
  const [localEconomy, setLocalEconomy] = useState(economy);
  const today = new Date().toISOString().split('T')[0];
  
  // Economy state'ini gerçek zamanlı güncelle
  useEffect(() => {
    const updateEconomy = () => {
      try {
        const currentEconomy = JSON.parse(localStorage.getItem('economy') || '{}');
        setLocalEconomy(currentEconomy);
        setEconomy(currentEconomy);
      } catch (error) {
        console.error('Error updating economy:', error);
      }
    };
    
    updateEconomy();
    
    // Event listener ekle
    window.addEventListener('economyUpdated', updateEconomy);
    
    return () => {
      window.removeEventListener('economyUpdated', updateEconomy);
    };
  }, [setEconomy]);
  
  // Sekme değiştiğinde ve behaviors değiştiğinde state'i yenile
  useEffect(() => {
    if (currentTab === 'overview') {
      try {
        const currentEconomy = JSON.parse(localStorage.getItem('economy') || '{}');
        setLocalEconomy(currentEconomy);
        setEconomy(currentEconomy);
      } catch (error) {
        console.error('Error refreshing economy:', error);
      }
    }
  }, [currentTab, behaviors, setEconomy]);
  
  useEffect(() => {
    try {
      const today = new Date().toLocaleDateString();
      const saved = JSON.parse(localStorage.getItem("submittedToday")) || { date: today, list: [] };

      if (saved.date !== today) {
        localStorage.setItem("submittedToday", JSON.stringify({ date: today, list: [] }));
        setSubmitted([]);
      } else {
        setSubmitted(saved.list || []);
      }
    } catch (error) {
      console.error('Error loading submitted data:', error);
      setSubmitted([]);
    }
  }, []);
  
  function handleSubmit(id) {
    try {
      const card = document.getElementById(`student-${id}`);
      if (card) {
        card.classList.add("glow-fade");
        setTimeout(() => {
          try {
            const today = new Date().toLocaleDateString();
            const saved = JSON.parse(localStorage.getItem("submittedToday")) || { date: today, list: [] };
            const updatedList = [...new Set([...saved.list, id])];
            const updated = { date: today, list: updatedList };
            setSubmitted(updatedList);
            localStorage.setItem("submittedToday", JSON.stringify(updated));
          } catch (error) {
            console.error('Error saving submission:', error);
          }
        }, 600);
      }
    } catch (error) {
      console.error('Error handling submit:', error);
    }
  }
  
  function handleMarkUnsubmitted() {
    try {
      const notSubmitted = students.filter(s => !submitted.includes(s.id));
      const currentBehaviors = JSON.parse(localStorage.getItem('behaviors') || '{}');
      
      notSubmitted.forEach(s => {
        const key = `${s.id}-D09-${today}`;
        currentBehaviors[key] = (currentBehaviors[key] || 0) + 1;
      });
      
      localStorage.setItem('behaviors', JSON.stringify(currentBehaviors));
      setBehaviors(currentBehaviors);
      alert("Teslim etmeyen öğrenciler için D09 davranış kaydı eklendi!");
    } catch (error) {
      console.error('Error marking unsubmitted:', error);
      alert('İşlem sırasında hata oluştu!');
    }
  }
  
  function handleResetDay() {
    try {
      const today = new Date().toLocaleDateString();
      localStorage.setItem("submittedToday", JSON.stringify({ date: today, list: [] }));
      setSubmitted([]);
      alert("🔄 Yeni gün başlatıldı! Tüm öğrenciler geri getirildi.");
    } catch (error) {
      console.error('Error resetting day:', error);
      alert('Gün sıfırlanırken hata oluştu!');
    }
  }
  

  
  // Akademik puan hesaplama - devre dışı
  const getAcademicPoints = (studentId) => {
    return 0;
  };
  
  // Davranış ceza hesaplama
  const getBehaviorPenalty = (studentId) => {
    try {
      let totalPenalty = 0;
      const customBehaviors = JSON.parse(localStorage.getItem('customBehaviors') || '[]');
      const allBehaviors = [...behaviorIssues, ...customBehaviors.filter(cb => !behaviorIssues.find(bi => bi.code === cb.code))];
      
      // Tüm davranış kayıtlarını kontrol et
      Object.keys(behaviors).forEach(key => {
        if (key.startsWith(`${studentId}-`)) {
          const parts = key.split('-');
          const behaviorCode = parts[1];
          const count = behaviors[key] || 0;
          
          const behavior = allBehaviors.find(b => b.code === behaviorCode);
          if (behavior && count > 0) {
            let penalty = 0;
            switch(behavior.code) {
              case 'D01': penalty = count * 20; break;
              case 'D02': penalty = count * 20; break;
              case 'D03': penalty = count * 5; break;
              default: 
                const customBehavior = customBehaviors.find(cb => cb.code === behavior.code);
                penalty = count * (customBehavior?.penalty || 3);
                break;
            }
            totalPenalty += penalty;
          }
        }
      });
      return totalPenalty;
    } catch (error) {
      console.error('Error calculating behavior penalty:', error);
      return 0;
    }
  };
  
  const getTeacherReward = (studentId) => {
    return localEconomy[`earnings-${studentId}`] || 0;
  };
  
  const getSpending = (studentId) => {
    return localEconomy[`spending-${studentId}`] || 0;
  };
  
  // Liderlik sıralaması için (harcama dahil değil)
  const getTotalMoney = (studentId) => {
    const teacherReward = getTeacherReward(studentId);
    const behaviorPenalty = getBehaviorPenalty(studentId);
    return teacherReward - behaviorPenalty;
  };
  
  // Alışveriş için bakiye kontrolü (harcama dahil)
  const getAvailableBalance = (studentId) => {
    const teacherReward = getTeacherReward(studentId);
    const behaviorPenalty = getBehaviorPenalty(studentId);
    const spending = getSpending(studentId);
    return teacherReward - behaviorPenalty - spending;
  };
  
  const addTeacherReward = (studentId, points) => {
    const current = getTeacherReward(studentId);
    onUpdateEconomy(`earnings-${studentId}`, current + points);
  };
  
  const addCleanBehaviorReward = () => {
    try {
      const currentBehaviors = JSON.parse(localStorage.getItem('behaviors') || '{}');
      const currentEconomy = JSON.parse(localStorage.getItem('economy') || '{}');
      const customBehaviors = JSON.parse(localStorage.getItem('customBehaviors') || '[]');
      const allBehaviors = [...behaviorIssues, ...customBehaviors.filter(cb => !behaviorIssues.find(bi => bi.code === cb.code))];
      
      let count = 0;
      students.forEach(student => {
        const hasIssues = allBehaviors.some(behavior => {
          const key = `${student.id}-${behavior.code}-${today}`;
          return (currentBehaviors[key] || 0) > 0;
        });
        if (!hasIssues) {
          const current = currentEconomy[`earnings-${student.id}`] || 0;
          currentEconomy[`earnings-${student.id}`] = current + 30;
          count++;
        }
      });
      
      localStorage.setItem('economy', JSON.stringify(currentEconomy));
      setLocalEconomy(currentEconomy);
      setEconomy(currentEconomy);
      alert(`${count} öğrenciye +30 para verildi!`);
    } catch (error) {
      console.error('Error adding clean behavior reward:', error);
      alert('Ödül verilirken hata oluştu!');
    }
  };
  
  const storeItems = [
    { id: 1, name: "Öğretmenim ve Annemle Hafta Sonu Gezisi", price: 1250, emoji: "🚌" },
    { id: 2, name: "Öğretmen Asistanı", price: 1000, emoji: "📚" },
    { id: 3, name: "Sıra Lideri", price: 950, emoji: "⭐" },
    { id: 4, name: "Teneffüs Lideri", price: 900, emoji: "🏃" },
    { id: 5, name: "Serbest Kıyafet", price: 800, emoji: "👕" },
    { id: 6, name: "Akıllı Tahtadan İçerik Seçme", price: 800, emoji: "📺" },
    { id: 7, name: "VIP Fotoğraf + Panoya Asılma", price: 750, emoji: "📸" },
    { id: 8, name: "Oyun Seçme Hakkı", price: 750, emoji: "🎲" },
    { id: 9, name: "Kantin Alışveriş Kuponu", price: 750, emoji: "🛒" },
    { id: 10, name: "Veliye Özel Teşekkür", price: 500, emoji: "📜" }
  ];
  
  const rewards = [
    { id: 1, name: "Törende Uygun Davranış", points: 15 },
    { id: 2, name: "Derse Katılım", points: 10 },
    { id: 3, name: "Haftanın MVP", points: 25 },
    { id: 4, name: "Öğretmen Teşekkürü", points: 20 },
    { id: 5, name: "Yardımlaşma", points: 12 },
    { id: 6, name: "Temizlik Ödülü", points: 10 },
    { id: 7, name: "Misafire Saygı", points: 20 },
    { id: 8, name: "Eskiye göre daha gayretli olma ve kurallara uygun davranma", points: 200 }
  ];
  
  const buyItem = (studentId, item) => {
    const student = students.find(s => s.id === studentId);
    const balance = getAvailableBalance(studentId);
    
    if (balance >= item.price) {
      // Create a custom styled confirmation dialog
      const modal = document.createElement('div');
      modal.style.cssText = `
        position: fixed;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        background: rgba(0,0,0,0.7);
        display: flex;
        justify-content: center;
        align-items: center;
        z-index: 10000;
        font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
      `;
      
      const dialog = document.createElement('div');
      dialog.style.cssText = `
        background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
        padding: 40px;
        border-radius: 25px;
        box-shadow: 0 25px 60px rgba(0,0,0,0.3);
        text-align: center;
        max-width: 450px;
        width: 90%;
        border: 4px solid #ffd700;
        position: relative;
        overflow: hidden;
      `;
      
      // XSS koruması: student.name'i sanitize et
      const safeName = String(student.name).replace(/[<>"'&]/g, '').toUpperCase();
      const safeItemName = String(item.name).replace(/[<>"'&]/g, '');
      const safeEmoji = String(item.emoji).replace(/[<>"'&]/g, '');
      
      dialog.innerHTML = `
        <div style="
          position: absolute;
          top: -50%;
          left: -50%;
          width: 200%;
          height: 200%;
          background: linear-gradient(45deg, transparent, rgba(255,255,255,0.1), transparent);
          animation: shimmer 3s linear infinite;
        "></div>
        <div style="font-size: 4rem; margin-bottom: 20px; animation: bounce 2s infinite;">${safeEmoji}</div>
        <h2 style="
          color: white;
          font-size: 2rem;
          font-weight: 900;
          margin: 0 0 15px 0;
          text-shadow: 3px 3px 6px rgba(0,0,0,0.5);
          letter-spacing: 1px;
        ">${safeName}</h2>
        <div style="
          background: rgba(255,255,255,0.2);
          padding: 25px;
          border-radius: 15px;
          margin: 20px 0;
          backdrop-filter: blur(10px);
        ">
          <h3 style="
            color: #ffd700;
            font-size: 1.8rem;
            margin: 0 0 15px 0;
            text-shadow: 2px 2px 4px rgba(0,0,0,0.3);
          ">${safeItemName}</h3>
          <div style="display: flex; justify-content: space-between; margin: 15px 0;">
            <span style="color: white; font-size: 1.3rem; font-weight: 600;">💰 Fiyat:</span>
            <span style="color: #ffd700; font-size: 1.5rem; font-weight: 900;">${item.price}₺</span>
          </div>
          <div style="display: flex; justify-content: space-between; margin: 15px 0;">
            <span style="color: white; font-size: 1.3rem; font-weight: 600;">💳 Bakiye:</span>
            <span style="color: #90ee90; font-size: 1.5rem; font-weight: 900;">${balance}₺</span>
          </div>
          <div style="display: flex; justify-content: space-between; margin: 15px 0; padding-top: 15px; border-top: 2px solid rgba(255,255,255,0.3);">
            <span style="color: white; font-size: 1.3rem; font-weight: 600;">💸 Kalan:</span>
            <span style="color: #87ceeb; font-size: 1.5rem; font-weight: 900;">${balance - item.price}₺</span>
          </div>
        </div>
        <div style="display: flex; gap: 15px; justify-content: center; margin-top: 30px;">
          <button id="confirmBtn" style="
            background: linear-gradient(45deg, #27ae60, #2ecc71);
            color: white;
            border: none;
            padding: 15px 30px;
            border-radius: 50px;
            font-size: 1.2rem;
            font-weight: 900;
            cursor: pointer;
            box-shadow: 0 8px 25px rgba(39, 174, 96, 0.4);
            transition: all 0.3s ease;
          ">✅ SATIN AL</button>
          <button id="cancelBtn" style="
            background: linear-gradient(45deg, #e74c3c, #c0392b);
            color: white;
            border: none;
            padding: 15px 30px;
            border-radius: 50px;
            font-size: 1.2rem;
            font-weight: 900;
            cursor: pointer;
            box-shadow: 0 8px 25px rgba(231, 76, 60, 0.4);
            transition: all 0.3s ease;
          ">❌ İPTAL</button>
        </div>
      `;
      
      // Add CSS animations
      const style = document.createElement('style');
      style.textContent = `
        @keyframes shimmer {
          0% { transform: translateX(-100%) translateY(-100%) rotate(45deg); }
          100% { transform: translateX(100%) translateY(100%) rotate(45deg); }
        }
        @keyframes bounce {
          0%, 20%, 50%, 80%, 100% { transform: translateY(0); }
          40% { transform: translateY(-10px); }
          60% { transform: translateY(-5px); }
        }
      `;
      document.head.appendChild(style);
      
      modal.appendChild(dialog);
      document.body.appendChild(modal);
      
      // Add button hover effects
      const confirmBtn = dialog.querySelector('#confirmBtn');
      const cancelBtn = dialog.querySelector('#cancelBtn');
      
      confirmBtn.addEventListener('mouseenter', () => {
        confirmBtn.style.transform = 'translateY(-3px) scale(1.05)';
        confirmBtn.style.boxShadow = '0 12px 35px rgba(39, 174, 96, 0.6)';
      });
      confirmBtn.addEventListener('mouseleave', () => {
        confirmBtn.style.transform = 'translateY(0) scale(1)';
        confirmBtn.style.boxShadow = '0 8px 25px rgba(39, 174, 96, 0.4)';
      });
      
      cancelBtn.addEventListener('mouseenter', () => {
        cancelBtn.style.transform = 'translateY(-3px) scale(1.05)';
        cancelBtn.style.boxShadow = '0 12px 35px rgba(231, 76, 60, 0.6)';
      });
      cancelBtn.addEventListener('mouseleave', () => {
        cancelBtn.style.transform = 'translateY(0) scale(1)';
        cancelBtn.style.boxShadow = '0 8px 25px rgba(231, 76, 60, 0.4)';
      });
      
      // Handle button clicks
      confirmBtn.addEventListener('click', () => {
        document.body.removeChild(modal);
        document.head.removeChild(style);
        
        // Proceed with purchase
        // Eski sistem için toplam harcama
        const currentSpending = getSpending(studentId);
        onUpdateEconomy(`spending-${studentId}`, currentSpending + item.price);
        
        // Yeni sistem için detaylı alışveriş geçmişi - güvenli kaydetme
        try {
          const spendingData = JSON.parse(localStorage.getItem(`spending-${studentId}`) || '[]');
          spendingData.push({
            item: safeItemName,
            amount: item.price,
            date: new Date().toISOString()
          });
          localStorage.setItem(`spending-${studentId}`, JSON.stringify(spendingData));
        } catch (error) {
          console.error('Error saving purchase history:', error);
        }
        
        alert(`✅ ${safeName} için "${safeItemName}" başarıyla satın alındı!`);
      });
      
      cancelBtn.addEventListener('click', () => {
        document.body.removeChild(modal);
        document.head.removeChild(style);
      });
      
      // Close on background click
      modal.addEventListener('click', (e) => {
        if (e.target === modal) {
          document.body.removeChild(modal);
          document.head.removeChild(style);
        }
      });
    }
  };
  
  const refundItem = (studentId, purchaseIndex, amount) => {
    try {
      const student = students.find(s => s.id === studentId);
      const safeName = String(student.name).replace(/[<>"'&]/g, '');
      
      if (window.confirm(`${safeName} için ${amount}₺ iade edilsin mi?`)) {
        // Detaylı geçmişten sil
        const spendingData = JSON.parse(localStorage.getItem(`spending-${studentId}`) || '[]');
        spendingData.splice(purchaseIndex, 1);
        localStorage.setItem(`spending-${studentId}`, JSON.stringify(spendingData));
        
        // Economy'den harcamayı düş
        const currentSpending = getSpending(studentId);
        onUpdateEconomy(`spending-${studentId}`, Math.max(0, currentSpending - amount));
        
        alert(`✅ ${safeName} için ${amount}₺ iade edildi!`);
        setCurrentTab('history'); // Refresh
      }
    } catch (error) {
      console.error('Error processing refund:', error);
      alert('İade işlemi sırasında hata oluştu!');
    }
  };
  
  const toggleStudent = (studentId) => {
    setSelectedStudents(prev => 
      prev.includes(studentId) 
        ? prev.filter(id => id !== studentId)
        : [...prev, studentId]
    );
  };
  
  const selectAll = () => {
    setSelectedStudents(students.map(s => s.id));
  };
  
  const clearAll = () => {
    setSelectedStudents([]);
  };
  
  return (
    <div className="economy-view">
      <h2>💰 Sınıf Ekonomisi</h2>
      

      
      <div className="economy-tabs">
        <button className={`tab ${currentTab === 'overview' ? 'active' : ''}`} onClick={() => setCurrentTab('overview')}>
          📊 Genel Durum
        </button>
        <button className={`tab ${currentTab === 'store' ? 'active' : ''}`} onClick={() => setCurrentTab('store')}>
          🏪 Mağaza
        </button>
        <button className={`tab ${currentTab === 'rewards' ? 'active' : ''}`} onClick={() => setCurrentTab('rewards')}>
          🎁 Ödül Ver
        </button>
        <button className={`tab ${currentTab === 'history' ? 'active' : ''}`} onClick={() => setCurrentTab('history')}>
          📋 Alışveriş Geçmişi
        </button>
        <button className={`tab ${currentTab === 'homeworkCheck' ? 'active' : ''}`} onClick={() => setCurrentTab('homeworkCheck')}>
          📘 Ödev Teslimi
        </button>
      </div>
      
      {currentTab === 'overview' && (
        <div className="overview-section">
          <button className="daily-flowers-btn" onClick={addCleanBehaviorReward}>
            🎆 Bugünün Çiçeklerine +30 Para
          </button>
          
          <h3>🏆 Sınıf Liderleri (Otomatik Sıralama)</h3>
          <div className="students-economy">
            {students
              .map(student => ({ ...student, totalMoney: getTotalMoney(student.id) }))
              .sort((a, b) => b.totalMoney - a.totalMoney)
              .map((student, index) => {
                const academic = getAcademicPoints(student.id);
                const teacherReward = getTeacherReward(student.id);
                const behaviorPenalty = getBehaviorPenalty(student.id);
                const spending = getSpending(student.id);
                const customBehaviors = JSON.parse(localStorage.getItem('customBehaviors') || '[]');
                const allBehaviors = [...behaviorIssues, ...customBehaviors.filter(cb => !behaviorIssues.find(bi => bi.code === cb.code))];
                const isCleanBehavior = allBehaviors.every(behavior => {
                  const key = `${student.id}-${behavior.code}-${today}`;
                  return (behaviors[key] || 0) === 0;
                });
                
                return (
                  <div key={student.id} className={`student-economy-card ${isCleanBehavior ? 'clean-behavior' : ''}`}>
                    <div className="student-info">
                      <div>
                        <span className="rank">{index + 1}</span>
                        <h4>{student.name}</h4>
                      </div>
                      <div className={`total-money ${student.totalMoney < 0 ? 'negative' : ''}`}>{student.totalMoney}₺</div>
                    </div>
                    <div className="economy-avatar">
                      <img 
                        src={process.env.PUBLIC_URL + (student.avatar || '/images/avatars/default.png')} 
                        alt={student.name}
                        onError={(e) => e.target.src = process.env.PUBLIC_URL + '/images/avatars/test.png'}
                      />
                    </div>
                    
                    <div className="points-info">
                      <div>
                        <span className="label">Öğretmen Ödül:</span>
                        <span className="value positive">+{teacherReward}₺</span>
                      </div>
                      <div>
                        <span className="label">Davranış Ceza:</span>
                        <span className="value negative">-{behaviorPenalty}₺</span>
                      </div>
                      <div>
                        <span className="label">Mağaza Bakiyesi:</span>
                        <span className={`value ${getAvailableBalance(student.id) >= 0 ? 'positive' : 'negative'}`}>{getAvailableBalance(student.id)}₺</span>
                      </div>
                    </div>
                    
                    <p style={{fontSize: '0.9rem', color: '#7f8c8d', textAlign: 'center', marginTop: '10px'}}>
                      Otomatik hesaplama: Öğretmen ödülü - Davranış cezası
                    </p>
                  </div>
                );
              })
            }
          </div>
        </div>
      )}
      
      {currentTab === 'store' && (
        <div className="store-section">
          <h3>🏪 Ödül Mağazası</h3>
          <div className="store-items">
            {storeItems.sort((a, b) => b.price - a.price).map(item => (
              <div key={item.id} className="store-item">
                <div className="item-info">
                  <span className="emoji">{item.emoji}</span>
                  <span className="name">{item.name}</span>
                  <span className="price">{item.price}₺</span>
                </div>
                <select onChange={(e) => {
                  const studentId = Number(e.target.value);
                  if (studentId) {
                    buyItem(studentId, item);
                    e.target.value = '';
                  }
                }}>
                  <option value="">Öğrenci seç...</option>
                  {students
                    .filter(student => getAvailableBalance(student.id) >= item.price)
                    .map(student => (
                      <option key={student.id} value={student.id}>
                        {student.name} ({getAvailableBalance(student.id)}₺)
                      </option>
                    ))
                  }
                </select>
              </div>
            ))}
          </div>
        </div>
      )}
      
      {currentTab === 'rewards' && (
        <div className="rewards-section">
          <div className="rewards-layout">
            <div className="students-selection-panel">
              <div className="selection-header">
                <h4>👥 Öğrenci Seçimi</h4>
                <div className="selection-buttons">
                  <button className="select-all-btn" onClick={selectAll}>
                    Hepsini Seç
                  </button>
                  <button className="clear-selection-btn" onClick={clearAll}>
                    Seçimi İptal Et
                  </button>
                </div>
              </div>
              
              <div className="students-checkbox-list">
                {students.map(student => (
                  <div 
                    key={student.id} 
                    className={`student-checkbox-item ${selectedStudents.includes(student.id) ? 'selected' : ''}`}
                    onClick={() => toggleStudent(student.id)}
                  >
                    <span className="student-name">{student.name}</span>
                  </div>
                ))}
              </div>
              
              <div className="selection-summary">
                {selectedStudents.length} öğrenci seçili
              </div>
            </div>
            
            <div className="rewards-panel">
              <h4>🎁 Ödül Kategorileri</h4>
              <div className="reward-items">
                {rewards.map(reward => (
                  <div key={reward.id} className="reward-item">
                    <div className="reward-info">
                      <span className="reward-name">{reward.name}</span>
                      <span className="reward-points">+{reward.points}₺</span>
                    </div>
                    <button 
                      className="give-reward-btn"
                      disabled={selectedStudents.length === 0}
                      onClick={() => {
                        try {
                          // localStorage'dan güncel veriyi oku
                          const currentEconomy = JSON.parse(localStorage.getItem('economy') || '{}');
                          
                          // Tüm güncellemeleri tek objede topla
                          const updates = {};
                          selectedStudents.forEach(studentId => {
                            const current = currentEconomy[`earnings-${studentId}`] || 0;
                            updates[`earnings-${studentId}`] = current + reward.points;
                          });
                          
                          // Tek seferde merge et ve kaydet
                          const newEconomy = { ...currentEconomy, ...updates };
                          localStorage.setItem('economy', JSON.stringify(newEconomy));
                          setEconomy(newEconomy);
                          
                          // Ödül alkış sesi
                          playCheer();
                          
                          alert(`${selectedStudents.length} öğrenciye ${reward.points}₺ ödül verildi!`);
                          setSelectedStudents([]);
                        } catch (error) {
                          console.error('Error giving reward:', error);
                          alert('Ödül verilirken hata oluştu!');
                        }
                      }}
                    >
                      {selectedStudents.length > 0 
                        ? `${selectedStudents.length} Öğrenciye Ver`
                        : 'Öğrenci Seç'
                      }
                    </button>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}
      
      {currentTab === 'history' && (
        <div className="history-section">
          <div className="section-header">
            <h3>📋 Alışveriş Geçmişi</h3>
            <button 
              className="delete-all-btn"
              onClick={() => {
                if (window.confirm('Tüm öğrencilerin alışveriş geçmişini silmek istediğinizden emin misiniz?')) {
                  try {
                    students.forEach(student => {
                      localStorage.removeItem(`spending-${student.id}`);
                    });
                    alert('Tüm alışveriş geçmişi silindi!');
                    setCurrentTab('history'); // Refresh
                  } catch (error) {
                    console.error('Error deleting all history:', error);
                    alert('Geçmiş silinirken hata oluştu!');
                  }
                }
              }}
            >
              🗑️ Tümünü Sil
            </button>
          </div>
          
          <div className="history-cards">
            {students.map(student => {
              let spendingData = [];
              let totalSpent = 0;
              
              try {
                spendingData = JSON.parse(localStorage.getItem(`spending-${student.id}`) || '[]');
                totalSpent = spendingData.reduce((sum, item) => sum + item.amount, 0);
              } catch (error) {
                console.error('Error parsing spending data:', error);
              }
              
              if (spendingData.length === 0) return null;
              
              return (
                <div key={student.id} className="history-card">
                  <div className="card-header">
                    <div className="student-avatar">{student.name.charAt(0)}</div>
                    <div className="student-details">
                      <h4>{student.name}</h4>
                      <span className="student-number">No: {student.schoolNumber}</span>
                    </div>
                    <div className="total-badge">{totalSpent}₺</div>
                  </div>
                  
                  <div className="purchases-list">
                    {spendingData.map((purchase, index) => (
                      <div key={index} className="purchase-item">
                        <div className="purchase-info">
                          <span className="item-name">{purchase.item}</span>
                          <span className="purchase-date">{new Date(purchase.date).toLocaleDateString('tr-TR')}</span>
                        </div>
                        <div className="purchase-actions">
                          <span className="purchase-amount">{purchase.amount}₺</span>
                          <button 
                            className="refund-btn"
                            onClick={() => refundItem(student.id, index, purchase.amount)}
                            title="İade Et"
                          >
                            ↩️
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                  
                  <button 
                    className="delete-card-btn"
                    onClick={() => {
                      if (window.confirm(`${student.name}'in alışveriş geçmişini silmek istediğinizden emin misiniz?`)) {
                        localStorage.removeItem(`spending-${student.id}`);
                        alert(`${student.name}'in alışveriş geçmişi silindi!`);
                        setCurrentTab('history'); // Refresh
                      }
                    }}
                  >
                    🗑️ Geçmişi Sil
                  </button>
                </div>
              );
            })}
          </div>
          
          {students.every(student => {
            try {
              const spendingData = JSON.parse(localStorage.getItem(`spending-${student.id}`) || '[]');
              return spendingData.length === 0;
            } catch (error) {
              console.error('Error parsing spending data:', error);
              return true;
            }
          }) && (
            <div className="empty-history">
              <div className="empty-icon">🛒</div>
              <h3>Henüz alışveriş geçmişi yok</h3>
              <p>Öğrenciler mağazadan alışveriş yaptığında burada görünecek</p>
            </div>
          )}
        </div>
      )}
      
      {currentTab === 'homeworkCheck' && (
        <div className="homework-check">
          <h2>📘 Ödev Teslimi</h2>
          <div className="homework-grid">
            {students.map(s => (
              <div
                key={s.id}
                id={`student-${s.id}`}
                className={`student-card ${submitted.includes(s.id) ? 'submitted' : ''}`}
                onClick={() => !submitted.includes(s.id) && handleSubmit(s.id)}
                aria-pressed={submitted.includes(s.id) ? 'true' : 'false'}
              >
                <span>{s.name}</span>
                <small>📘 Ödevimi Teslim Ettim</small>
              </div>
            ))}
          </div>

          <button className="mark-unsubmitted" onClick={handleMarkUnsubmitted}>
            📝 Ödevi Teslim Edilmedi
          </button>
          
          <button className="reset-day" onClick={handleResetDay}>
            🔄 Günü Sıfırla
          </button>
        </div>
      )}
    </div>
  );
}

// Behaviors View Component
function BehaviorsView({ students, behaviorIssues, behaviors, onSaveBehavior, setBehaviors }) {
  // Özel davranışları birleştir - güvenli parsing
  const customBehaviors = useMemo(() => {
    try {
      return JSON.parse(localStorage.getItem('customBehaviors') || '[]');
    } catch (error) {
      console.error('Error parsing custom behaviors:', error);
      return [];
    }
  }, []);
  const allBehaviorIssues = [...behaviorIssues, ...customBehaviors.filter(cb => !behaviorIssues.find(bi => bi.code === cb.code))];
  const [selectedStudents, setSelectedStudents] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  
  const today = new Date().toISOString().split('T')[0];
  
  // Türkçe karakter desteği için normalize fonksiyonu
  const normalizeText = (text) => {
    return text
      .replace(/İ/g, 'i')
      .replace(/I/g, 'i')
      .replace(/ı/g, 'i')
      .replace(/Ğ/g, 'g')
      .replace(/ğ/g, 'g')
      .replace(/Ü/g, 'u')
      .replace(/ü/g, 'u')
      .replace(/Ş/g, 's')
      .replace(/ş/g, 's')
      .replace(/Ö/g, 'o')
      .replace(/ö/g, 'o')
      .replace(/Ç/g, 'c')
      .replace(/ç/g, 'c')
      .toLowerCase();
  };
  
  const filteredStudents = students.filter(student => 
    normalizeText(student.name).includes(normalizeText(searchTerm)) ||
    student.schoolNumber.includes(searchTerm)
  );
  
  const getBehaviorCount = (studentId, behaviorCode) => {
    const key = `${studentId}-${behaviorCode}-${today}`;
    return behaviors[key] || 0;
  };
  
  const updateBehaviorCount = (behaviorCode, increment) => {
    if (selectedStudents.length === 0) return;
    
    try {
      const currentBehaviors = JSON.parse(localStorage.getItem('behaviors') || '{}');
      
      selectedStudents.forEach(studentId => {
        const key = `${studentId}-${behaviorCode}-${today}`;
        const currentCount = currentBehaviors[key] || 0;
        currentBehaviors[key] = Math.max(0, currentCount + increment);
      });
      
      localStorage.setItem('behaviors', JSON.stringify(currentBehaviors));
      
      // Davranış cezası ses efekti
      if (increment > 0) {
        playCrowdWarning();
      }
      
      // State'i güncelle (sayfa yenileme YOK)
      setBehaviors(currentBehaviors);
    } catch (error) {
      console.error('Error updating behavior count:', error);
      alert('Davranış kaydı güncellenirken hata oluştu!');
    }
  };
  
  const selectAll = () => {
    setSelectedStudents(filteredStudents.map(s => s.id));
  };
  
  const clearAll = () => {
    setSelectedStudents([]);
  };
  
  return (
    <div className="behaviors-view">
      <h2>✍️ <span className="text-gradient">Seçimlerin seni anlatır; davranışın en büyük imzandır...</span> ✨</h2>
      <p className="date-info">🌟 <span className="text-gradient">Bu imza, toplumdaki saygınlığını belirler.</span> 👀</p>
      
      {/* Behavior Tracking */}
      {selectedStudents.length > 0 && (
        <div className="behavior-tracking">
          <div className="behavior-categories">
            <div className="serious-incidents">
              <h4>🚨 Ciddi Olaylar</h4>
              {allBehaviorIssues.filter(b => b.type === 'serious_incident').map(behavior => (
                <div key={behavior.code} className="behavior-item serious">
                  <div className="behavior-info">
                    <span className="behavior-code">{behavior.code}</span>
                    <span className="behavior-desc">{behavior.description}</span>
                  </div>
                  <div className="behavior-controls">
                    <button 
                      className="count-btn minus"
                      onClick={() => updateBehaviorCount(behavior.code, -1)}
                    >
                      -
                    </button>
                    <span className="count">{selectedStudents.reduce((total, studentId) => total + getBehaviorCount(studentId, behavior.code), 0)}</span>
                    <button 
                      className="count-btn plus"
                      onClick={() => updateBehaviorCount(behavior.code, 1)}
                    >
                      +
                    </button>
                  </div>
                </div>
              ))}
              

              
              {/* Positive Behavior Encouragement Card */}
              <div className="teacher-upset-card">
                <div className="upset-icon">⚠️</div>
                <div className="upset-content">
                  <h4 className="upset-title">KÖTÜYÜ TERCİH ETMEN</h4>
                  <p className="upset-subtitle">Sadece kendine değil, ailene ve sınıfına da zarar verir.</p>
                </div>
                <div className="upset-decoration">💭</div>
              </div>
            </div>
            
            <div className="daily-counts">
              <h4>📊 Günlük Sayımlar</h4>
              {allBehaviorIssues.filter(b => b.type === 'daily_count').map(behavior => (
                <div key={behavior.code} className="behavior-item daily">
                  <div className="behavior-info">
                    <span className="behavior-code">{behavior.code}</span>
                    <span className="behavior-desc">{behavior.description}</span>
                  </div>
                  <div className="behavior-controls">
                    <button 
                      className="count-btn minus"
                      onClick={() => updateBehaviorCount(behavior.code, -1)}
                    >
                      -
                    </button>
                    <span className="count">{selectedStudents.reduce((total, studentId) => total + getBehaviorCount(studentId, behavior.code), 0)}</span>
                    <button 
                      className="count-btn plus"
                      onClick={() => updateBehaviorCount(behavior.code, 1)}
                    >
                      +
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
      
      {/* Selected Students Header */}
      <div className="selected-students-header">
        <h3>Seçili Öğrenciler ({selectedStudents.length})</h3>
        {selectedStudents.length > 0 && (
          <div className="selected-students-list">
            {selectedStudents.map(studentId => {
              const student = students.find(s => s.id === studentId);
              return (
                <span key={studentId} className="selected-student-tag">
                  {student?.name}
                </span>
              );
            })}
          </div>
        )}
      </div>
      
      {/* Student Search */}
      <div className="student-search">
        <input
          type="text"
          placeholder="🔍 Öğrenci ara (isim veya numara)..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="search-input"
        />
        <div className="selection-controls">
          <button className="select-all-btn" onClick={selectAll}>
            Hepsini Seç
          </button>
          <button className="clear-btn" onClick={clearAll}>
            Temizle
          </button>
          <span className="selected-count">
            {selectedStudents.length} öğrenci seçili
          </span>
        </div>
      </div>
      
      {/* Student Selection */}
      <div className="students-selection">
        <h3>Öğrenci Seçimi ({filteredStudents.length})</h3>
        <div className="students-grid compact">
          {filteredStudents.map(student => (
            <button
              key={student.id}
              className={`student-card compact ${selectedStudents.includes(student.id) ? 'selected' : ''}`}
              onClick={() => {
                setSelectedStudents(prev => 
                  prev.includes(student.id) 
                    ? prev.filter(id => id !== student.id)
                    : [...prev, student.id]
                );
              }}
            >
              <h4>{student.name}</h4>
              <p>No: {student.schoolNumber}</p>
            </button>
          ))}
        </div>
      </div>
      
      {selectedStudents.length === 0 && (
        <div className="no-selection">
          <p>👆 Davranış kaydı için yukarıdan öğrencileri seçin</p>
        </div>
      )}
    </div>
  );
}



function App() {
  const [isReady, setIsReady] = useState(false);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [currentView, setCurrentView] = useState('home');

  const [behaviors, setBehaviors] = useState({});
  const [economy, setEconomy] = useState({});
  const [password, setPassword] = useState('');
  const [loginError, setLoginError] = useState('');
  
  // Always call useTheme unconditionally
  const theme = useTheme();

  // Tema bazlı arka plan
  useEffect(() => {
    if (theme && theme.colors) {
      const gradient = `linear-gradient(135deg, ${theme.colors.primary} 0%, ${theme.colors.secondary} 100%)`;
      document.body.style.background = gradient;
    }
    
    // Cleanup
    return () => {
      document.body.style.background = '';
      document.body.style.animation = '';
      document.body.classList.remove('special-day-effects');
    };
  }, [theme]);

  // Authentication check
  useEffect(() => {
    try {
      const auth = localStorage.getItem('teacherAuth');
      setIsAuthenticated(auth === 'true');
      
      // Load data from localStorage first with error handling
      const savedBehaviors = localStorage.getItem('behaviors');
      const savedEconomy = localStorage.getItem('economy');
      const savedView = localStorage.getItem('currentView');
      
      if (savedBehaviors) {
        try {
          setBehaviors(JSON.parse(savedBehaviors));
        } catch (error) {
          console.error('Error parsing behaviors:', error);
          setBehaviors({});
        }
      }
      
      if (savedEconomy) {
        try {
          setEconomy(JSON.parse(savedEconomy));
        } catch (error) {
          console.error('Error parsing economy:', error);
          setEconomy({});
        }
      }
      
      if (savedView && savedView !== 'home') setCurrentView(savedView);
      
      // 100ms sonra hazır olduğunu belirt
      setTimeout(() => setIsReady(true), 100);
    } catch (error) {
      console.error('Error during initialization:', error);
      setIsReady(true);
    }
  }, []);



  // Save behavior
  const saveBehavior = async (studentId, behaviorCode, count) => {
    const today = new Date().toISOString().split('T')[0];
    const key = `${studentId}-${behaviorCode}-${today}`;
    const newBehaviors = { ...behaviors, [key]: count };
    setBehaviors(newBehaviors);
    localStorage.setItem('behaviors', JSON.stringify(newBehaviors));
    

  };

  // Update economy
  const updateEconomy = (key, value) => {
    const newEconomy = { ...economy, [key]: value };
    setEconomy(newEconomy);
    localStorage.setItem('economy', JSON.stringify(newEconomy));
  };

  // Navigation with persistence
  const handleViewChange = (view) => {
    setCurrentView(view);
    localStorage.setItem('currentView', view);
  };

  // Login
  const handleLogin = () => {
    const validPassword = process.env.REACT_APP_TEACHER_PASSWORD || '2303';
    if (password === validPassword) {
      localStorage.setItem('teacherAuth', 'true');
      setIsAuthenticated(true);
      setLoginError('');
    } else {
      setLoginError('Hatalı şifre!');
    }
    setPassword(''); // Güvenlik: Şifreyi her durumda temizle
  };

  // Logout
  const handleLogout = () => {
    localStorage.removeItem('teacherAuth');
    setIsAuthenticated(false);
  };



  // Yükleme ekranı
  if (!isReady) {
    return (
      <div style={{
        position: 'fixed',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        background: 'linear-gradient(135deg, #8b0000 0%, #ff0000 40%, #ffd700 100%)',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        color: 'white',
        fontSize: '24px',
        fontWeight: 'bold'
      }}>
        Yükleniyor...
      </div>
    );
  }
  
  if (!isAuthenticated) {
    return (
      <div className="auth-screen">
        <HeroBanner theme={theme} />
        <LoginCard 
          password={password}
          setPassword={setPassword}
          loginError={loginError}
          handleLogin={handleLogin}
          theme={theme}
        />
      </div>
    );
  }

  return (
    <div className="app">
      <header className="header">
        <div className="header-info">
          <h1>{classInfo.school}</h1>
          <h2>{classInfo.className} - <span style={{fontWeight: 'bold'}}>{classInfo.teacher}</span></h2>
        </div>
        <button className="logout-btn" onClick={handleLogout}>
          🚪 Çıkış
        </button>
      </header>

      {currentView !== 'home' && (
        <nav className="nav">
          <button 
            className="nav-btn active"
            onClick={() => handleViewChange('home')}
          >
            🏠 Ana Sayfaya Dön
          </button>
        </nav>
      )}

      <main className="main">
        {currentView === 'home' && (
          <div className="home-view">
            <h2>🌸 Sevgili Öğrencilerim</h2>
            
            {(() => {
              // Doğum günü kontrolü
              const birthdayCheck = checkBirthdayPriority();
              
              return (
                <div style={{
                  position: "relative",
                  zIndex: 99999,
                  opacity: 1,
                  display: "block",
                  width: "100%",
                  textAlign: "center",
                  marginBottom: "25px",
                  padding: "20px 10px",
                  background: theme.colors ? `linear-gradient(135deg, ${theme.colors.primary}, ${theme.colors.secondary})` : "linear-gradient(135deg, #8b0000, #ffd700)",
                  borderRadius: "12px",
                  color: "#fff",
                  fontWeight: "bold",
                  fontSize: "2.5rem",
                  letterSpacing: "1px",
                  boxShadow: "0 0 20px rgba(0,0,0,0.3)",
                  textShadow: "1px 1px 4px rgba(0,0,0,0.5)"
                }}>
                  {birthdayCheck.hasBirthday ? (
                    // Doğum günü mesajı (avatar yok, sadece metin)
                    <p className="birthday-card-message" style={{
                      fontSize: "1.8rem",
                      fontWeight: "bold",
                      margin: 0,
                      color: "#fff"
                    }}>
                      🎂 {getBirthdayCardMessage(birthdayCheck.birthdayStudent)}
                    </p>
                  ) : theme.type === 'special' ? (
                    // Özel gün mesajı
                    <>
                      {theme.icon} {theme.title}
                      <div style={{ fontSize: "1rem", fontWeight: "normal", marginTop: "5px" }}>
                        {theme.message}
                      </div>
                    </>
                  ) : (
                    // Normal gün Atatürk sözü
                    <p className="quote" style={{
                      fontSize: "1.5rem",
                      fontWeight: "normal",
                      marginTop: "5px",
                      margin: 0
                    }}>
                      "{theme.message}"<br/>
                      <strong className="quote-author">{NORMAL_DAY_QUOTE_AUTHOR}</strong>
                    </p>
                  )}
                </div>
              );
            })()}
            
            {/* Ana Menü Kartları - Üst Sıra */}
            <div className="menu-cards top-row">
              <div className="menu-card behaviors" onClick={() => handleViewChange('behaviors')}>
                <div className="icon">⭐</div>
                <h3>Davranış Takibi</h3>
                <p>Davranış kategorisi ile günlük takip ve kayıt sistemi</p>
              </div>
              
              <div className="menu-card weekly-test" onClick={() => handleViewChange('weekly-test')}>
                <div className="icon">📝</div>
                <h3>Haftalık Test</h3>
                <p>15 soruluk haftalık test değerlendirmesi 💡</p>
              </div>
              
              <div className="menu-card story-exam" onClick={() => handleViewChange('story-exam')}>
                <div className="icon">📖</div>
                <h3>Hikaye Sınavı</h3>
                <p>10 soruluk hikaye sınavı değerlendirmesi 📚</p>
              </div>
            </div>

            {/* Ana Menü Kartları - Alt Sıra */}
            <div className="menu-cards bottom-row">
              <div className="menu-card economy" onClick={() => handleViewChange('economy')}>
                <div className="icon">💰</div>
                <h3>Sınıf Ekonomisi</h3>
                <p>Para sistemi, liderlik tablosu ve ödül mağazası</p>
              </div>
              
              <div className="menu-card telegram" onClick={() => handleViewChange('telegram')}>
                <div className="icon">📱</div>
                <h3>Telegram Mesajları</h3>
                <p>Velilere otomatik davranış ve durum mesajları</p>
              </div>
              
              <div className="menu-card admin" onClick={() => handleViewChange('admin')}>
                <div className="icon">⚙️</div>
                <h3>Yönetim Paneli</h3>
                <p>Öğrenci yönetimi, sistem ayarları ve gelişmiş raporlama</p>
              </div>
            </div>

            {/* Hızlı İşlemler */}
            <div className="quick-actions">
              <h3>⚡ Hızlı Mesaj Gönder</h3>
              <div className="quick-buttons">
                <button className="quick-btn behavior pulse" onClick={() => {
                  // Günlük davranış mesajı gönder
                  handleViewChange('telegram');
                }}>
                  <div className="btn-icon">📤</div>
                  <div className="btn-content">
                    <div className="btn-title">Günlük Davranış Mesajı</div>
                    <div className="btn-subtitle">Velilere bugünkü davranış durumu</div>
                  </div>
                  <div className="btn-arrow">→</div>
                </button>
                <button className="quick-btn academic pulse" onClick={() => {
                  // Başarı durumu mesajı gönder
                  handleViewChange('telegram');
                }}>
                  <div className="btn-icon">🏆</div>
                  <div className="btn-content">
                    <div className="btn-title">Başarı Durumunu Veliye Gönder</div>
                    <div className="btn-subtitle">Akademik başarı ve genel durum</div>
                  </div>
                  <div className="btn-arrow">→</div>
                </button>
              </div>
            </div>
            
            {/* İstatistikler */}
            <div className="stats-grid">
              <div className="stat-card">
                <h3>👥 Öğrenci Sayısı</h3>
                <p className="stat-number">{students.length}</p>
              </div>
              <div className="stat-card">
                <h3>📝 Haftalık Testler</h3>
                <p className="stat-number">{(() => {
                  try {
                    return JSON.parse(localStorage.getItem('weeklyTests') || '[]').length;
                  } catch { return 0; }
                })()}</p>
              </div>
              <div className="stat-card">
                <h3>💰 Toplam Para</h3>
                <p className="stat-number">{(() => {
                  try {
                    return Object.values(JSON.parse(localStorage.getItem('economy') || '{}')).reduce((total, amount) => total + (amount || 0), 0);
                  } catch { return 0; }
                })()}₺</p>
              </div>
              <div className="stat-card">
                <h3>⚠️ Davranış Kayıtları</h3>
                <p className="stat-number">{(() => {
                  try {
                    return Object.values(JSON.parse(localStorage.getItem('behaviors') || '{}')).reduce((total, count) => total + count, 0);
                  } catch { return 0; }
                })()}</p>
              </div>
            </div>
          </div>
        )}



        {currentView === 'behaviors' && (
          <BehaviorsView 
            students={students}
            behaviorIssues={behaviorIssues}
            behaviors={behaviors}
            onSaveBehavior={saveBehavior}
            setBehaviors={setBehaviors}
          />
        )}

        {currentView === 'economy' && (
          <EconomyView 
            students={students}
            behaviors={behaviors}
            behaviorIssues={behaviorIssues}
            economy={economy}
            onUpdateEconomy={updateEconomy}
            setEconomy={setEconomy}
            setBehaviors={setBehaviors}
          />
        )}

        {currentView === 'telegram' && (
          <TelegramView 
            students={students}
            behaviors={behaviors}
            behaviorIssues={behaviorIssues}
          />
        )}

        {currentView === 'export' && (
          <ExportView 
            students={students}
            behaviors={behaviors}
            behaviorIssues={behaviorIssues}
            economy={economy}
          />
        )}

        {currentView === 'admin' && (
          <AdminPanel 
            students={students}
            behaviors={behaviors}
            behaviorIssues={behaviorIssues}
            economy={economy}
            curriculum={{}}
            onClose={() => handleViewChange('home')}
            onUpdateStudents={(updatedStudents) => {
              localStorage.setItem('updatedStudents', JSON.stringify(updatedStudents));
              window.location.reload();
            }}
            onUpdateCurriculum={() => {}}
          />
        )}

        {currentView === 'weekly-test' && (
          <WeeklyTestView students={students} />
        )}

        {currentView === 'story-exam' && (
          <StoryExamView students={students} />
        )}
      </main>
    </div>
  );
}

export default App;